import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Layout } from "../components/Layout";
import { Seo } from "../components/Seo";
import { openConsultationModal } from "../components/ConsultationModal";
import {
  ShieldCheck, Terminal, CreditCard, ScanFace, Video, BarChart3,
  ArrowRight, ExternalLink, Zap, Lock, Server, CheckCircle2, ChevronRight,
  Activity, Layers, Shield, Users, GitCommit, Database, Check,
  RefreshCw, Cpu, Gauge, FileText, AlertTriangle, Cloud, Globe,
  DollarSign, Bell, MessageSquare, Radio, Key, Laptop, Code2,
  Clock, Award, AlertCircle, Calendar, IndianRupee, Fingerprint,
  MonitorCheck, ShieldAlert, GitPullRequest, ArrowUpRight, Copy, CheckCheck, Boxes, X,
  QrCode, MapPin, Search, Paperclip, ChevronDown, CheckCircle, Mail, Briefcase
} from "lucide-react";

export default function Norbit() {
  // Selected engine for progressive disclosure detail modal
  const [selectedEngine, setSelectedEngine] = useState(null);
  // Interactive state for Multi-Scenario Workflow Explorer
  const [selectedScenarioIndex, setSelectedScenarioIndex] = useState(0);
  const [activeWorkflowStep, setActiveWorkflowStep] = useState(0);
  // Dashboard screenshot view toggle (1 = Overview/Command, 2 = Scrolled Modules)
  const [dashboardView, setDashboardView] = useState(1);
  // Copy state for quick commands
  const [copiedIndex, setCopiedIndex] = useState(null);
  // Interactive preview tabs for Core Engines
  const [devopsTab, setDevopsTab] = useState("staging");
  const [paymentTab, setPaymentTab] = useState("qr");
  const [portalTab, setPortalTab] = useState("dashboard");

  // Keyboard accessibility: Escape key closes engine detail modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setSelectedEngine(null);
      }
    };
    if (selectedEngine) {
      window.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [selectedEngine]);

  const copyToClipboard = (text, idx) => {
    navigator.clipboard?.writeText(text);
    setCopiedIndex(idx);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "nORBIT v2.0",
    "alternateName": "Neuravolt Operational & Resource Business Intelligence Technology",
    "headline": "The Sovereign Enterprise Operating System",
    "description": "One Platform. Your Entire Business. Replace 8+ fragmented SaaS tools with one sovereign in-house stack: Guardian SRE, Staging DevOps, NeuraCollect UPI & Ledger, Team Chat, HRMS BioAuth, and White-Label Client Portals.",
    "applicationCategory": "EnterpriseApplication",
    "operatingSystem": "Linux, Cloud, Bare Metal, Air-Gapped",
    "provider": {
      "@type": "Organization",
      "name": "Neuravolt",
      "legalName": "VOLTNOVATE SOLUTIONS LLP",
      "url": "https://neuravolt.in",
    },
    "offers": {
      "@type": "Offer",
      "price": "Custom",
      "priceCurrency": "INR",
      "availability": "https://schema.org/InStock",
    },
    "featureList": [
      "Guardian Autonomous SRE & 2.8s Auto-Heal Cyber Shield",
      "Staging-First DevOps & Scope Creep Protection with SRE Stopwatch",
      "NeuraCollect Dynamic UPI QR & Double-Entry Ledger (0% Platform Cuts)",
      "In-House Team Chat & PIN-Secured MeetPass Video Calls",
      "Workforce HRM, Branch Geofenced BioAuth & WebAuthn Passkeys",
      "Multi-Tenant White-Label Client Portal with Transparent SLA Clocks",
      "DPDP-2023 Compliant Sovereign On-Premise or Private Cloud Architecture"
    ],
  };

  // ──────────────────────────────────────────────────────────────────────────
  // 2. THE 6 CORE ENGINES (VERIFIED ENTERPRISE CAPABILITIES)
  // ──────────────────────────────────────────────────────────────────────────
  const coreEngines = [
    {
      id: "guardian-sre",
      num: "01",
      category: "SRE & CYBER DEFENSE",
      title: "Guardian Autonomous SRE & Cyber Shield",
      valueProp: "Monitor infrastructure, throttle CPU spikes with cgroups, and auto-quarantine threats.",
      capabilities: [
        { label: "Host telemetry & cgroups", icon: Activity },
        { label: "Auto-quarantine & voice siren", icon: ShieldCheck },
        { label: "Asset radar & BYOVPS", icon: Globe }
      ],
      metricBadge: "2.8s Auto-Heal • Zero Midnight Crises",
      tagline: "Your 24/7 autonomous site reliability engineer that never sleeps.",
      icon: ShieldCheck,
      color: "blue",
      dedicatedPageUrl: "/services/guardian-sre",
      bullets: [
        {
          label: "Autonomous Threat Quarantine",
          detail: "2-second webshell isolation, brute-force IP auto-ban via nftables, and process SIGSTOP."
        },
        {
          label: "Linux cgroup Resource Guard",
          detail: "Proactive CPU & memory throttling via systemctl set-property to prevent runaway scripts from crashing host VPS."
        },
        {
          label: "Mobile Real-Time Voice Sirens",
          detail: "Synthesized verbal audio alarms dispatched directly to the on-call engineer's mobile device during P0 incidents."
        },
        {
          label: "Single-Command BYOVPS Node Setup",
          detail: "Connect any Linux VPS (Hetzner, AWS, GCP, On-Premise) to automated SRE monitoring with a single curl command."
        }
      ],
      terminalPreview: {
        title: "guardian-sre-sentinel",
        status: "Active Shield • Inode Watch Active",
        command: "guardian-daemon --kernel-ebpf --quarantine-auto",
        logs: [
          "[KERNEL] eBPF telemetry hooked into /proc/kcore",
          "[THREAT] Malicious file drop detected on /var/www/uploads/c99.php",
          "[AUTO-HEAL] Process SIGSTOP dispatched + chmod 000 (1.42s latency)",
          "[CGROUP] Applied CPUQuota=50% on runaway user slice via systemd",
          "[SIREN] Spoken voice alert synthesized to on-call engineer mobile PWA"
        ]
      }
    },
    {
      id: "staging-devops",
      num: "02",
      category: "DEVOPS & DELIVERY",
      title: "DevOps Delivery & Scope Creep Protection",
      valueProp: "Manage Git task synchronization, SRE developer stopwatches, and client AMC retainer quotas.",
      capabilities: [
        { label: "GitHub PR task sync", icon: GitPullRequest },
        { label: "AMC retainer stopwatch", icon: Clock },
        { label: "Change request shield", icon: RefreshCw }
      ],
      metricBadge: "Zero Unbilled Hours • Real-Time Quotas",
      tagline: "Bridge the gap between engineering commits, client deliverables, and billable revenue.",
      icon: GitPullRequest,
      color: "amber",
      bullets: [
        {
          label: "GitHub PR Status Synchronization",
          detail: "PR branches (e.g. task/nv-812) automatically update linked development task cards and review approvals."
        },
        {
          label: "Live SRE Stopwatch",
          detail: "Track coding hours directly on task cards, auto-deducting time from the client's monthly AMC quota (with 80% usage alerts)."
        },
        {
          label: "Change Request (CR) Shield",
          detail: "Turn out-of-scope WhatsApp or email feature requests into instant, billable milestone quotes instead of working for free."
        },
        {
          label: "Unified Task Kanban",
          detail: "Subtasks, sprint milestones, and time logs connected directly to client billing without duplicate entry."
        }
      ],
      terminalPreview: {
        title: "staging-release-gate",
        status: "PR Sync: Active • Task Mesh Online",
        command: "norbit-sync --pr 204 --track-task NV-812",
        logs: [
          "[GITHUB] PR #204 mapped to Task #NV-812 via branch regex parser",
          "[CI/CD] Unit test verification passed: 48 tests passing (0 regressions)",
          "[STOPWATCH] Active timer logged: 03h 45m dedicated to milestone delivery",
          "[AMC QUOTA] Client Retainer: 34.5 / 40.0 hrs consumed (86% usage alert sent)",
          "[CR SHIELD] Out-of-scope API endpoint quoted as Addon #CR-12"
        ]
      }
    },
    {
      id: "neuracollect-ledger",
      num: "03",
      category: "FINANCE & PAYMENTS",
      title: "NeuraCollect & Operational Cash Flow Ledger",
      valueProp: "Connect dynamic UPI collections and gateway webhooks with automated cash flow and P&L registers.",
      capabilities: [
        { label: "Dynamic UPI QR & links", icon: IndianRupee },
        { label: "Cashfree & Razorpay webhooks", icon: CheckCheck },
        { label: "P&L cash flow registers", icon: FileText }
      ],
      metricBadge: "0% Platform Cuts • Live P&L",
      tagline: "Frictionless collections with automated cash flow reconciliation.",
      icon: CreditCard,
      color: "cyan",
      bullets: [
        {
          label: "Dynamic UPI QR Engine",
          detail: "Instant, zero-fee dynamic QR codes and payment links generated on every milestone invoice."
        },
        {
          label: "Multi-Gateway Webhook Sync",
          detail: "Cashfree and Razorpay webhooks reconcile payments into invoice status with HMAC-SHA256 signature verification."
        },
        {
          label: "Operational Master Ledger",
          detail: "Payments, expenses, purchases, and payroll sync into real-time operational P&L and Cash Flow registers."
        },
        {
          label: "Autonomous 9:00 AM Chaser Cron",
          detail: "Automated daily cron chasing overdue invoices on Day 1, 3, and 7 via WhatsApp and email with direct payment links."
        }
      ],
      terminalPreview: {
        title: "neuracollect-webhook-sink",
        status: "0% Gateway Cuts • Ledger Synced",
        command: "neuracollect --listen-upi --reconcile-ledger",
        logs: [
          "[INVOICE] #NV-2026-114 generated for Retainer Settlement (₹1,50,000)",
          "[UPI QR] Dynamic VPA QR generated with transaction note ref_901924",
          "[WEBHOOK] Instant settlement verified via HMAC-SHA256 signature in 3ms",
          "[REGISTER] Logged Bank Transaction entry · Real-time P&L Statement refreshed",
          "[CHASER] Scheduled 9:00 AM follow-up cancelled for paid invoice"
        ]
      }
    },
    {
      id: "team-chat",
      num: "04",
      category: "TEAM & WORKSPACE COMMS",
      title: "In-House Team Chat, Mailbox & MeetPass",
      valueProp: "Unified native business email, real-time channel chat with Guardian DLP, and PIN-secured video calls.",
      capabilities: [
        { label: "In-house business mailbox", icon: Mail },
        { label: "Real-time chat with DLP", icon: MessageSquare },
        { label: "PIN-secured meetings", icon: Lock }
      ],
      metricBadge: "Zero Per-User Seat Taxes • Built-In DLP",
      tagline: "Real-time company communication without third-party surveillance.",
      icon: MessageSquare,
      color: "purple",
      bullets: [
        {
          label: "In-House Native Mailbox",
          detail: "Built-in enterprise email client (/mailbox) allowing teams to send, receive, and organize business emails without Google Workspace."
        },
        {
          label: "Guardian DLP Secret Redaction",
          detail: "Automatically detects and redacts leaked AWS keys, SSH private keys, database URIs, GitHub tokens, and Stripe keys before storage."
        },
        {
          label: "MeetPass Video & Audio",
          detail: "Branded, PIN-secured client video meeting rooms with zero third-party Zoom or Google Meet links."
        },
        {
          label: "Contextual Task Conversion",
          detail: "Convert any team chat message or support email into an active sprint task with one click."
        }
      ],
      terminalPreview: {
        title: "norbit-webrtc-gateway",
        status: "Private Mesh Active • DLP Active",
        command: "meetpass --generate-room --pin-lock --dlp-guard",
        logs: [
          "[MAILBOX] Native IMAP/SMTP pool connected for business domains",
          "[DLP] Detected AWS_ACCESS_KEY in message: auto-redacted to [REDACTED_AWS_KEY:••••4921]",
          "[SOCKET] Channel #engineering connected: 18 active peers online",
          "[MEETPASS] Room created: meet.neuravolt.in/arch-review (PIN: 819-204)",
          "[ACTION] Message 09:41 converted to Sprint Task #TK-992 with 1 click"
        ]
      }
    },
    {
      id: "workforce-hrm",
      num: "05",
      category: "PEOPLE & IDENTITY",
      title: "Workforce HRM, Geofence & Dynamic RBAC",
      valueProp: "Manage employee lifecycle with GPS branch geofencing, biometric passkeys, and a 40+ permission RBAC matrix.",
      capabilities: [
        { label: "Branch geofencing", icon: MapPin },
        { label: "Biometric WebAuthn passkeys", icon: Users },
        { label: "40+ Dynamic RBAC matrix", icon: Key }
      ],
      metricBadge: "DPDP-2023 Compliant • FIDO2 Passkeys",
      tagline: "Modern employee lifecycle with ironclad identity protection.",
      icon: Users,
      color: "emerald",
      bullets: [
        {
          label: "Branch GPS Geofencing",
          detail: "Strict GPS boundary radius check ensures verified on-premise attendance check-in/out."
        },
        {
          label: "Biometric WebAuthn Passkeys",
          detail: "128-d Euclidean face vector matching with liveness detection and replay attack defense (REPLAY_DISTANCE = 0.03)."
        },
        {
          label: "40+ Dynamic Roles Matrix",
          detail: "Toggle granular permissions across user, project, task, billing, and SRE resources via interactive UI checkboxes."
        },
        {
          label: "Token-Based Zero-Password Invites",
          detail: "Single-use cryptographic invitation tokens eliminate insecure default passwords for new hires."
        }
      ],
      terminalPreview: {
        title: "hrm-identity-sentinel",
        status: "DPDP-2023 Enforced • Vector In Local DB",
        command: "hrm-auth --check-geofence --verify-passkey",
        logs: [
          "[GPS] Employee lat/long verified within Noida HQ geofence radius (28.5355, 77.3910)",
          "[WEBAUTHN] Hardware credential validated: TouchID/Face key handle verified",
          "[BIO-VECTOR] 128-D facial vector matched locally (Euclidean distance: 0.28 < 0.60)",
          "[REPLAY] Replay attack check passed (Distance > 0.03 threshold)",
          "[RBAC] Session granted: Role 'Senior DevOps Lead' (34 permissions active)"
        ]
      }
    },
    {
      id: "client-portal",
      num: "06",
      category: "CLIENT EXPERIENCE",
      title: "Multi-Tenant White-Label Client Portal",
      valueProp: "Give clients one place to track work, approve quotes, submit tickets, and pay invoices with an internal security fence.",
      capabilities: [
        { label: "Milestone & task tracking", icon: Laptop },
        { label: "Attachment security fence", icon: ShieldCheck },
        { label: "SLA resolution clocks", icon: Clock }
      ],
      metricBadge: "100% Data Isolation • Transparent SLAs",
      tagline: "Give every client a branded self-service command center.",
      icon: Laptop,
      color: "indigo",
      bullets: [
        {
          label: "Attachment Security Boundary (isInternal)",
          detail: "Internal developer notes and drafts marked isInternal: true are filtered out by backend queries so agency notes never leak."
        },
        {
          label: "Live Deliverable Tracking",
          detail: "Clients inspect sprint milestones, review deliverable progress, and issue digital sign-offs."
        },
        {
          label: "SLA Resolution Countdown Clocks",
          detail: "P0–P3 ticket escalation with transparent SLA countdowns and 1-click ticket-to-task conversion."
        },
        {
          label: "Monday 9:00 AM Executive Reports",
          detail: "Automated weekly SLA health and hours-logged executive summary dispatched every Monday morning."
        }
      ],
      terminalPreview: {
        title: "client-tenant-subdomain",
        status: "Tenant: Acme Corp • Strict Isolation Active",
        command: "tenant-session --org acme --verify-token",
        logs: [
          "[PORTAL] Authenticated admin@acmecorp.com on portal.acmecorp.com",
          "[SECURITY] Filtered internal attachments: where: { isInternal: false } enforced",
          "[UAT] Client reviewed deliverable milestone v2.4 (1-click approval signed)",
          "[TICKET] P1 SLA Clock: 02h 45m remaining (Engineering Lead assigned)",
          "[EXECUTIVE] Weekly SLA health digest compiled for automated Monday 9 AM dispatch"
        ]
      }
    }
  ];

  // ──────────────────────────────────────────────────────────────────────────
  // 3. MULTI-SCENARIO WORKFLOW EXPLORER DATA (VERIFIED EVENT PIPELINES)
  // ──────────────────────────────────────────────────────────────────────────
  // ──────────────────────────────────────────────────────────────────────────
  // 3. AUTOMATED WORKFLOW EXPLORER DATA (5 VERIFIED DOMAINS)
  // ──────────────────────────────────────────────────────────────────────────
  const workflowScenarios = [
    {
      id: "client-revenue",
      title: "Client & Revenue",
      desc: "From lead to payment",
      icon: Briefcase,
      steps: [
        {
          num: "01",
          title: "Deal Closed",
          desc: "Quotation signed and client onboarded",
          status: "TRIGGERED",
          statusType: "orange",
          iconColor: "text-amber-500",
          iconBg: "bg-amber-50",
          icon: FileText,
          detailTitle: "Deal Closed / Won",
          detailDesc: "When a quotation is signed, nORBIT creates the client organization, provisions portal access and prepares the project or AMC contract.",
          trigger: "Quotation signed",
          source: "Sales / CRM Module",
          creates: "Client, Portal User, Invoice (AMC/Project)",
          notification: "Welcome email to client"
        },
        {
          num: "02",
          title: "Project Setup",
          desc: "Client portal access and workspace ready",
          status: "AUTOMATED",
          statusType: "green",
          iconColor: "text-emerald-500",
          iconBg: "bg-emerald-50",
          icon: Laptop,
          detailTitle: "Automated Project & Workspace Provisioning",
          detailDesc: "System instantly spins up the client workspace, isolates tenant database schema, and emails credentials for the white-labeled portal.",
          trigger: "Client organization created",
          source: "Tenant Manager Service",
          creates: "Isolated workspace, Subdomain & SSL config",
          notification: "Magic link invitation via NeuraMail"
        },
        {
          num: "03",
          title: "Work Tracking",
          desc: "Developer hours deduct from AMC",
          status: "AUTOMATED",
          statusType: "green",
          iconColor: "text-purple-500",
          iconBg: "bg-purple-50",
          icon: Clock,
          detailTitle: "Live Task Time & Retainer Debit",
          detailDesc: "Granular stopwatch logs and merged GitHub pull requests automatically deduct developer hours from the client's monthly retainer quota.",
          trigger: "Stopwatch stop / PR merged",
          source: "DevOps & Time Tracker",
          creates: "Cryptographic time entry & AMC deduction",
          notification: "Retainer balance alert to client manager"
        },
        {
          num: "04",
          title: "Payment & Ledger",
          desc: "Payment auto-reconcile to financial registers",
          status: "AUTOMATED",
          statusType: "green",
          iconColor: "text-rose-500",
          iconBg: "bg-rose-50",
          icon: CreditCard,
          detailTitle: "Direct Bank Settlement & Auto-Reconcile",
          detailDesc: "Dynamic UPI QR settlement verified via bank webhook and logged directly to GST-compliant financial registers with zero gateway cut.",
          trigger: "Bank webhook event (UPI / NEFT)",
          source: "NeuraCollect Gateway",
          creates: "GST invoice reconciliation & ledger journal",
          notification: "Instant WhatsApp & Email payment receipt"
        }
      ],
      recentEvents: [
        { time: "10:24 AM", dot: "orange", title: "Quotation signed", sub: "ACME Technologies" },
        { time: "10:24 AM", dot: "green", title: "Client organization created", sub: "Client ID: CLI-1023" },
        { time: "10:24 AM", dot: "green", title: "Portal access provisioned", sub: "client@acme.com" },
        { time: "10:25 AM", dot: "green", title: "Welcome email sent", sub: "via NeuraMail" }
      ]
    },
    {
      id: "devops-scope",
      title: "DevOps & Scope",
      desc: "Tasks, PRs & AMC",
      icon: Code2,
      steps: [
        {
          num: "01",
          title: "Sprint Milestone",
          desc: "Scope verified against contract MSA",
          status: "TRIGGERED",
          statusType: "orange",
          iconColor: "text-amber-500",
          iconBg: "bg-amber-50",
          icon: Boxes,
          detailTitle: "Scope Creep Defense Gate",
          detailDesc: "All assigned sprint backlog cards are cryptographically verified against the approved Master Services Agreement before work starts.",
          trigger: "Task assigned to milestone",
          source: "Project Delivery Hub",
          creates: "Approved sprint backlog allocation",
          notification: "Engineering squad sprint dispatch"
        },
        {
          num: "02",
          title: "Task Stopwatch",
          desc: "Active coding session bound to developer",
          status: "AUTOMATED",
          statusType: "green",
          iconColor: "text-emerald-500",
          iconBg: "bg-emerald-50",
          icon: Clock,
          detailTitle: "Active Developer SRE Stopwatch",
          detailDesc: "Developer runs live task timer in workspace. Sessions are signed and timestamped to guarantee zero unbilled engineering hours.",
          trigger: "Stopwatch started by engineer",
          source: "Developer Workspace Hub",
          creates: "Active time audit log & heartbeat",
          notification: "Sprint burn-down sync"
        },
        {
          num: "03",
          title: "PR Merged",
          desc: "GitHub webhook syncs commit with task ID",
          status: "AUTOMATED",
          statusType: "green",
          iconColor: "text-purple-500",
          iconBg: "bg-purple-50",
          icon: GitPullRequest,
          detailTitle: "GitHub PR Ingestion & Commit Verification",
          detailDesc: "GitHub webhook receives merged PR, extracts task ref #TSK-401, and advances task status to READY_FOR_STAGING automatically.",
          trigger: "github.pull_request.closed event",
          source: "GitHub Sync Webhook Service",
          creates: "Automated task state progression",
          notification: "QA squad deployment webhook"
        },
        {
          num: "04",
          title: "Retainer Debited",
          desc: "Balance auto-deducts; alerts if low quota",
          status: "AUTOMATED",
          statusType: "green",
          iconColor: "text-rose-500",
          iconBg: "bg-rose-50",
          icon: FileText,
          detailTitle: "Real-Time AMC Balance Ledger",
          detailDesc: "3.75 hours automatically deducted from Acme Corp's 40h retainer allocation with automated overage notifications.",
          trigger: "Task completion sign-off",
          source: "Financial Retainer Ledger",
          creates: "AMC balance update (24.25 / 40.0h)",
          notification: "Client dashboard usage digest"
        }
      ],
      recentEvents: [
        { time: "11:10 AM", dot: "orange", title: "Task #TSK-401 created", sub: "Stripe Webhook Handler" },
        { time: "11:12 AM", dot: "green", title: "Stopwatch started by Aditya", sub: "Session ID: usr_882" },
        { time: "02:57 PM", dot: "green", title: "GitHub PR #182 merged", sub: "commit #b7e21a ref linked" },
        { time: "02:58 PM", dot: "green", title: "Retainer debited 3.75 hrs", sub: "Balance: 24.25 / 40.0 hrs" }
      ]
    },
    {
      id: "payments-finance",
      title: "Payments & Finance",
      desc: "Collections & P&L",
      icon: CreditCard,
      steps: [
        {
          num: "01",
          title: "Invoice Billed",
          desc: "GST compliant bill with dynamic QR",
          status: "TRIGGERED",
          statusType: "orange",
          iconColor: "text-amber-500",
          iconBg: "bg-amber-50",
          icon: FileText,
          detailTitle: "Automated GST Invoicing",
          detailDesc: "System compiles approved milestones and billable hours into a GST-compliant tax invoice with auto-generated dynamic UPI payment QR.",
          trigger: "Milestone completion / Monthly schedule",
          source: "Invoicing & Revenue Hub",
          creates: "GST invoice #NVLT-2026-10-00012",
          notification: "Dispatched to client billing email"
        },
        {
          num: "02",
          title: "Dynamic UPI QR",
          desc: "Customer scans UPI intent from mobile banking",
          status: "AUTOMATED",
          statusType: "green",
          iconColor: "text-emerald-500",
          iconBg: "bg-emerald-50",
          icon: QrCode,
          detailTitle: "Dynamic Intent QR Generation",
          detailDesc: "NeuraCollect generates an encrypted UPI intent payload pre-filled with the exact invoiced amount and merchant VPA.",
          trigger: "Client views invoice or portal payment screen",
          source: "NeuraCollect Engine",
          creates: "Session UPI intent QR & payment link",
          notification: "Payment page view tracked"
        },
        {
          num: "03",
          title: "0% Gateway Cut",
          desc: "Funds route direct to ICICI Current Account",
          status: "AUTOMATED",
          statusType: "green",
          iconColor: "text-purple-500",
          iconBg: "bg-purple-50",
          icon: IndianRupee,
          detailTitle: "Direct Bank-to-Bank Settlement",
          detailDesc: "Zero intermediary deduction. Full ₹1,77,000.00 settles instantly into your sovereign corporate current bank account.",
          trigger: "Bank NPCI settlement signal",
          source: "Direct Bank Webhook Handler",
          creates: "₹0 fee settlement record",
          notification: "Finance head instant payment alert"
        },
        {
          num: "04",
          title: "Auto-Reconcile",
          desc: "Financial journals, P&L and GST registers sync",
          status: "AUTOMATED",
          statusType: "green",
          iconColor: "text-rose-500",
          iconBg: "bg-rose-50",
          icon: CheckCircle2,
          detailTitle: "General Ledger Auto-Reconciliation",
          detailDesc: "Invoice marked PAID, transaction credited to bank register, and journal entries posted to P&L accounts with zero manual data entry.",
          trigger: "Settlement webhook confirmed",
          source: "Financial Accounting Engine",
          creates: "Double-entry bank ledger & GST journal",
          notification: "Client received official receipt"
        }
      ],
      recentEvents: [
        { time: "09:30 AM", dot: "orange", title: "Invoice #NVLT-00012 generated", sub: "Amount: ₹1,77,000.00" },
        { time: "09:32 AM", dot: "green", title: "Dynamic UPI QR scanned", sub: "Client IP: 103.21.244.2" },
        { time: "09:33 AM", dot: "green", title: "Bank webhook verified (NPCI)", sub: "ICICI Current A/C credited" },
        { time: "09:33 AM", dot: "green", title: "Ledger reconciled automatically", sub: "Platform cut: ₹0.00 (0%)" }
      ]
    },
    {
      id: "workforce-hrm",
      title: "Workforce & HRM",
      desc: "Attendance & Operations",
      icon: Users,
      steps: [
        {
          num: "01",
          title: "BioAuth Check-in",
          desc: "WebAuthn facial / biometric verified on PWA",
          status: "TRIGGERED",
          statusType: "orange",
          iconColor: "text-amber-500",
          iconBg: "bg-amber-50",
          icon: Fingerprint,
          detailTitle: "Cryptographic Biometric Check-in",
          detailDesc: "Employees tap check-in on their mobile PWA. WebAuthn authenticates device biometrics without sending raw biometric data off-device.",
          trigger: "Employee taps Check-in",
          source: "Workforce Mobile PWA",
          creates: "Signed cryptographic check-in token",
          notification: "Daily team roster update"
        },
        {
          num: "02",
          title: "Geofence Verified",
          desc: "Office GPS boundary validated within 15 meters",
          status: "AUTOMATED",
          statusType: "green",
          iconColor: "text-emerald-500",
          iconBg: "bg-emerald-50",
          icon: MapPin,
          detailTitle: "Geofencing Boundary Validation",
          detailDesc: "Device GPS coordinates are evaluated against registered office polygon or authorized remote client site with fraud spoofing detection.",
          trigger: "Check-in token received",
          source: "HRM Attendance Engine",
          creates: "Verified location stamp (12.9716° N)",
          notification: "Attendance state: PRESENT_ON_PREMISE"
        },
        {
          num: "03",
          title: "Auto Timesheet",
          desc: "Working hours linked to sprint milestones",
          status: "AUTOMATED",
          statusType: "green",
          iconColor: "text-purple-500",
          iconBg: "bg-purple-50",
          icon: Calendar,
          detailTitle: "Timesheet & Shift Consolidation",
          detailDesc: "Attendance automatically links with developer task timers, preventing duplicate manual timesheet entry at month end.",
          trigger: "Shift duration calculation",
          source: "HRM Timesheet Service",
          creates: "Daily verified working hours log",
          notification: "Daily productivity digest to manager"
        },
        {
          num: "04",
          title: "Payroll Register",
          desc: "Salaries, leaves and tax deductions auto-sync",
          status: "AUTOMATED",
          statusType: "green",
          iconColor: "text-rose-500",
          iconBg: "bg-rose-50",
          icon: IndianRupee,
          detailTitle: "Automated Monthly Payroll Preparation",
          detailDesc: "Verified attendance days, approved leaves, and overtime calculations flow directly into the monthly salary and TDS registers.",
          trigger: "Monthly payroll run trigger",
          source: "HRM Payroll Hub",
          creates: "Salary slip drafts & bank disbursement sheet",
          notification: "Employee salary slip ready on portal"
        }
      ],
      recentEvents: [
        { time: "09:02 AM", dot: "orange", title: "WebAuthn BioAuth verified", sub: "Aditya (ID: usr_882)" },
        { time: "09:02 AM", dot: "green", title: "Geofence radius confirmed", sub: "Within 12m of Head Office" },
        { time: "09:03 AM", dot: "green", title: "Daily timesheet initiated", sub: "Bound to Sprint Milestone M-02" },
        { time: "06:30 PM", dot: "green", title: "Shift completed: 9h 28m", sub: "Attendance logged to payroll" }
      ]
    },
    {
      id: "security-infra",
      title: "Security & Infra",
      desc: "Monitoring & Response",
      icon: Shield,
      steps: [
        {
          num: "01",
          title: "Threat Detected",
          desc: "eBPF kernel probe detects malicious drop",
          status: "TRIGGERED",
          statusType: "orange",
          iconColor: "text-amber-500",
          iconBg: "bg-amber-50",
          icon: ShieldAlert,
          detailTitle: "eBPF Kernel Anomaly Radar",
          detailDesc: "Guardian Linux daemon hooks into socket and filesystem events, catching unauthorized webshell upload or anomalous CPU compute spikes.",
          trigger: "eBPF socket / inode event",
          source: "Guardian Linux Daemon",
          creates: "Threat incident #INC-9418 snapshot",
          notification: "SRE incident war-room channel"
        },
        {
          num: "02",
          title: "1.4s Neutralize",
          desc: "Autonomous SIGSTOP and chmod 000 applied",
          status: "AUTOMATED",
          statusType: "green",
          iconColor: "text-emerald-500",
          iconBg: "bg-emerald-50",
          icon: ShieldCheck,
          detailTitle: "Sub-Second Process Neutralization",
          detailDesc: "Within 1.4 seconds, autonomous daemon dispatches SIGSTOP, zeroes file execution permissions (chmod 000), and blocks offending IP via nftables.",
          trigger: "High confidence threat rule match",
          source: "Guardian Autonomous Healer",
          creates: "Process quarantine & firewall drop rule",
          notification: "Offending IP blocked on border gateway"
        },
        {
          num: "03",
          title: "cgroup Throttled",
          desc: "Systemd slice CPU & RAM capped at 50%",
          status: "AUTOMATED",
          statusType: "green",
          iconColor: "text-purple-500",
          iconBg: "bg-purple-50",
          icon: Cpu,
          detailTitle: "Host cgroup Resource Protection",
          detailDesc: "Guardian executes systemctl set-property CPUQuota=50% on runaway user slices, preventing host freeze and protecting neighboring containers.",
          trigger: "Host load average spike > 8.0",
          source: "Systemd Cgroups Manager",
          creates: "Resource throttle policy enforcement",
          notification: "Host stabilized: Load returned to 0.68"
        },
        {
          num: "04",
          title: "Voice Siren P0",
          desc: "Synthesized audio alert rings lead SRE device",
          status: "AUTOMATED",
          statusType: "green",
          iconColor: "text-rose-500",
          iconBg: "bg-rose-50",
          icon: Radio,
          detailTitle: "Emergency Synthesized Voice Siren",
          detailDesc: "High-priority BullMQ job synthesizes verbal audio siren and pushes it directly to the mobile device of the on-call SRE lead.",
          trigger: "P0 incident confirmation",
          source: "Emergency Voice Siren Worker",
          creates: "Push notification token & audio stream",
          notification: "Voice alert delivered to lead SRE mobile"
        }
      ],
      recentEvents: [
        { time: "02:14 AM", dot: "orange", title: "eBPF probe detected webshell", sub: "Drop at /var/www/uploads/c99.php" },
        { time: "02:14 AM", dot: "green", title: "Autonomous SIGSTOP dispatched", sub: "PID 9418 halted in 1.42s" },
        { time: "02:15 AM", dot: "green", title: "cgroup CPUQuota=50% applied", sub: "Host load stabilized at 0.68" },
        { time: "02:15 AM", dot: "green", title: "Voice siren played on on-call PWA", sub: "Acknowledged by lead SRE" }
      ]
    }
  ];

  const deploymentTopologies = [
    {
      type: "Private Cloud VPS",
      subtitle: "Hetzner • AWS • GCP • DigitalOcean",
      desc: "Single-command Docker deployment on your existing cloud provider. Zero cloud vendor lock-in with Caddy automated SSL.",
      specs: ["15-Minute Deployment", "Docker Compose Monolith", "Automated Let's Encrypt TLS", "BYO Cloud Provider"],
      badge: "Fastest Setup"
    },
    {
      type: "Dedicated Bare Metal",
      subtitle: "Hosted on neuravolt.cloud",
      desc: "Extreme NVMe performance on dedicated bare metal servers. Zero hypervisor overhead, isolated network switch, and sub-1ms local I/O.",
      specs: ["Zero Hypervisor Penalty", "Dedicated IPv4 & IPv6 Subnet", "Sub-1ms PostgreSQL I/O", "99.99% Hardware SLA"],
      badge: "Maximum Performance",
      featured: true
    },
    {
      type: "Air-Gapped Sovereign On-Premise",
      subtitle: "Corporate Datacenter / Hardware Server",
      desc: "Run completely disconnected behind corporate firewalls. 100% DPDP-2023 compliant with zero outbound external telemetry.",
      specs: ["Zero External Internet Calls", "Local PostgreSQL Database", "Hardware WebAuthn FIDO2", "Air-Gapped Data Privacy"],
      badge: "100% Sovereign"
    }
  ];

  // ──────────────────────────────────────────────────────────────────────────
  // ENGINE MICRO-PREVIEW RENDERER (FAITHFUL TO MINIMALIST REFERENCE)
  // ──────────────────────────────────────────────────────────────────────────
  const renderEnginePreview = (engineId) => {
    switch (engineId) {
      case "guardian-sre":
        return (
          <div className="rounded-2xl bg-neutral-50/70 border border-neutral-200/80 p-3.5 flex flex-col justify-between text-left h-[235px] transition-all">
            {/* Header */}
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-bold text-neutral-800 tracking-tight">Infrastructure Health</span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200/60 text-emerald-600 text-[10px] font-semibold">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                <span>Healthy</span>
              </span>
            </div>

            {/* Wave chart */}
            <div className="relative w-full h-9 mb-1">
              <svg className="w-full h-full overflow-visible" viewBox="0 0 160 36" preserveAspectRatio="none" fill="none">
                <defs>
                  <linearGradient id="guardianHealthGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10B981" stopOpacity="0.25" />
                    <stop offset="100%" stopColor="#10B981" stopOpacity="0.0" />
                  </linearGradient>
                </defs>
                <path
                  d="M0,24 Q20,10 40,18 T80,12 T120,20 T160,8 L160,36 L0,36 Z"
                  fill="url(#guardianHealthGrad)"
                />
                <path
                  d="M0,24 Q20,10 40,18 T80,12 T120,20 T160,8"
                  fill="none"
                  stroke="#10B981"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            {/* 3 Stats */}
            <div className="grid grid-cols-3 gap-1 py-1.5 border-y border-neutral-200/60 mb-2 text-center">
              <div>
                <div className="text-xs font-black text-neutral-900">12</div>
                <div className="text-[9px] text-neutral-500 flex items-center justify-center gap-1 mt-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  <span>Servers</span>
                </div>
              </div>
              <div>
                <div className="text-xs font-black text-neutral-900">2</div>
                <div className="text-[9px] text-neutral-500 flex items-center justify-center gap-1 mt-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#FF5A1F]" />
                  <span>Alerts</span>
                </div>
              </div>
              <div>
                <div className="text-xs font-black text-neutral-900">99.9%</div>
                <div className="text-[9px] text-neutral-500 flex items-center justify-center gap-1 mt-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  <span>Uptime</span>
                </div>
              </div>
            </div>

            {/* Recent Incidents */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] font-bold text-neutral-700">Recent Incidents</span>
                <span className="text-[9px] text-neutral-400 font-medium">View all</span>
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between text-[9px] text-neutral-600">
                  <span className="flex items-center gap-1.5 truncate">
                    <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0" />
                    <span className="truncate">Suspicious login blocked</span>
                  </span>
                  <span className="text-[8px] font-mono text-neutral-400 shrink-0 ml-1">2 min ago</span>
                </div>
                <div className="flex items-center justify-between text-[9px] text-neutral-600">
                  <span className="flex items-center gap-1.5 truncate">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#FF5A1F] shrink-0" />
                    <span className="truncate">Webshell isolated</span>
                  </span>
                  <span className="text-[8px] font-mono text-neutral-400 shrink-0 ml-1">12 min ago</span>
                </div>
                <div className="flex items-center justify-between text-[9px] text-neutral-600">
                  <span className="flex items-center gap-1.5 truncate">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#FF5A1F] shrink-0" />
                    <span className="truncate">High CPU process stopped</span>
                  </span>
                  <span className="text-[8px] font-mono text-neutral-400 shrink-0 ml-1">28 min ago</span>
                </div>
              </div>
            </div>
          </div>
        );

      case "staging-devops":
        return (
          <div className="rounded-2xl bg-neutral-50/70 border border-neutral-200/80 p-3.5 flex flex-col justify-between text-left h-[235px]">
            {/* Environment Tabs */}
            <div className="grid grid-cols-3 gap-1 bg-neutral-200/60 p-0.5 rounded-lg text-center mb-2">
              {["development", "staging", "production"].map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setDevopsTab(tab)}
                  className={`text-[9px] capitalize py-0.5 rounded transition-all cursor-pointer ${
                    devopsTab === tab
                      ? "bg-white text-[#FF5A1F] font-bold shadow-2xs border border-orange-200/50"
                      : "text-neutral-500 hover:text-neutral-800 font-medium"
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Stepper Pipeline */}
            <div className="space-y-1.5 pl-0.5 mb-2">
              <div className="flex items-center gap-2 text-[10px] text-neutral-700 font-medium">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                <span>Build</span>
              </div>
              <div className="flex items-center gap-2 text-[10px] text-neutral-700 font-medium">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                <span>Deploy to Staging</span>
              </div>
              <div className="flex items-center gap-2 text-[10px] font-bold text-[#FF5A1F]">
                <span className="w-3.5 h-3.5 rounded-full border-2 border-[#FF5A1F] flex items-center justify-center shrink-0">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#FF5A1F] animate-pulse" />
                </span>
                <span>Client UAT</span>
              </div>
              <div className="flex items-center gap-2 text-[10px] text-neutral-400 font-medium">
                <span className="w-3.5 h-3.5 rounded-full border border-neutral-300 shrink-0" />
                <span>Merge to Production</span>
              </div>
            </div>

            {/* Recent Deployments */}
            <div className="pt-2 border-t border-neutral-200/60">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] font-bold text-neutral-700">Recent Deployments</span>
                <span className="text-[9px] text-neutral-400 font-medium">View all</span>
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between text-[9px]">
                  <div className="flex items-center gap-1.5">
                    <Check className="w-3 h-3 text-emerald-500 shrink-0" />
                    <span className="font-mono text-neutral-700 font-medium">v2.1.0</span>
                    <span className="px-1.5 py-0.2 rounded bg-emerald-50 text-emerald-600 border border-emerald-200/50 text-[8px] font-medium">Staging</span>
                  </div>
                  <span className="text-[8px] font-mono text-neutral-400">12 min ago</span>
                </div>
                <div className="flex items-center justify-between text-[9px]">
                  <div className="flex items-center gap-1.5">
                    <Check className="w-3 h-3 text-blue-500 shrink-0" />
                    <span className="font-mono text-neutral-700 font-medium">v2.0.4</span>
                    <span className="px-1.5 py-0.2 rounded bg-blue-50 text-blue-600 border border-blue-200/50 text-[8px] font-medium">Production</span>
                  </div>
                  <span className="text-[8px] font-mono text-neutral-400">2 days ago</span>
                </div>
                <div className="flex items-center justify-between text-[9px]">
                  <div className="flex items-center gap-1.5">
                    <Check className="w-3 h-3 text-blue-500 shrink-0" />
                    <span className="font-mono text-neutral-700 font-medium">v2.0.3</span>
                    <span className="px-1.5 py-0.2 rounded bg-blue-50 text-blue-600 border border-blue-200/50 text-[8px] font-medium">Production</span>
                  </div>
                  <span className="text-[8px] font-mono text-neutral-400">5 days ago</span>
                </div>
              </div>
            </div>
          </div>
        );

      case "neuracollect-ledger":
        return (
          <div className="rounded-2xl bg-neutral-50/70 border border-neutral-200/80 p-3.5 flex flex-col justify-between text-left h-[235px]">
            {/* Top Tabs */}
            <div className="flex items-center gap-1.5 mb-1.5">
              <button
                type="button"
                onClick={() => setPaymentTab("qr")}
                className={`flex-1 flex items-center justify-center gap-1.5 py-1 px-2 rounded-lg text-[9px] transition-all cursor-pointer ${
                  paymentTab === "qr"
                    ? "bg-orange-50 border border-orange-200/70 text-[#FF5A1F] font-bold"
                    : "bg-white border border-neutral-200/60 text-neutral-500 hover:text-neutral-800 font-medium"
                }`}
              >
                <QrCode className="w-3 h-3" />
                <span>Scan & Pay</span>
              </button>
              <button
                type="button"
                onClick={() => setPaymentTab("link")}
                className={`flex-1 flex items-center justify-center gap-1.5 py-1 px-2 rounded-lg text-[9px] transition-all cursor-pointer ${
                  paymentTab === "link"
                    ? "bg-orange-50 border border-orange-200/70 text-[#FF5A1F] font-bold"
                    : "bg-white border border-neutral-200/60 text-neutral-500 hover:text-neutral-800 font-medium"
                }`}
              >
                <ExternalLink className="w-3 h-3" />
                <span>Payment Link</span>
              </button>
            </div>

            {paymentTab === "qr" ? (
              <div className="flex flex-col items-center justify-center py-0.5">
                <div className="p-1.5 bg-white rounded-xl border border-neutral-200/80 shadow-2xs group-hover:scale-105 transition-transform duration-300">
                  <svg className="w-16 h-16 sm:w-18 sm:h-18" viewBox="0 0 100 100" fill="none">
                    {/* Top Left Corner */}
                    <rect x="5" y="5" width="28" height="28" rx="4" fill="#0B0B0B" />
                    <rect x="9" y="9" width="20" height="20" rx="2" fill="white" />
                    <rect x="13" y="13" width="12" height="12" rx="1" fill="#0B0B0B" />
                    {/* Top Right Corner */}
                    <rect x="67" y="5" width="28" height="28" rx="4" fill="#0B0B0B" />
                    <rect x="71" y="9" width="20" height="20" rx="2" fill="white" />
                    <rect x="75" y="13" width="12" height="12" rx="1" fill="#0B0B0B" />
                    {/* Bottom Left Corner */}
                    <rect x="5" y="67" width="28" height="28" rx="4" fill="#0B0B0B" />
                    <rect x="9" y="71" width="20" height="20" rx="2" fill="white" />
                    <rect x="13" y="75" width="12" height="12" rx="1" fill="#0B0B0B" />
                    {/* Internal Pattern Blocks */}
                    <rect x="38" y="8" width="8" height="8" rx="1" fill="#0B0B0B" />
                    <rect x="52" y="8" width="6" height="6" rx="1" fill="#0B0B0B" />
                    <rect x="38" y="22" width="6" height="6" rx="1" fill="#0B0B0B" />
                    <rect x="50" y="20" width="8" height="8" rx="1" fill="#0B0B0B" />
                    <rect x="8" y="40" width="6" height="6" rx="1" fill="#0B0B0B" />
                    <rect x="20" y="40" width="8" height="8" rx="1" fill="#0B0B0B" />
                    <rect x="34" y="36" width="8" height="8" rx="1" fill="#0B0B0B" />
                    <rect x="46" y="36" width="12" height="12" rx="2" fill="#FF5A1F" />
                    <rect x="64" y="40" width="8" height="8" rx="1" fill="#0B0B0B" />
                    <rect x="78" y="38" width="6" height="6" rx="1" fill="#0B0B0B" />
                    <rect x="88" y="44" width="6" height="6" rx="1" fill="#0B0B0B" />
                    <rect x="38" y="52" width="6" height="6" rx="1" fill="#0B0B0B" />
                    <rect x="50" y="54" width="8" height="8" rx="1" fill="#0B0B0B" />
                    <rect x="64" y="52" width="6" height="6" rx="1" fill="#0B0B0B" />
                    <rect x="78" y="54" width="8" height="8" rx="1" fill="#0B0B0B" />
                    <rect x="38" y="68" width="8" height="8" rx="1" fill="#0B0B0B" />
                    <rect x="52" y="72" width="6" height="6" rx="1" fill="#0B0B0B" />
                    <rect x="68" y="68" width="10" height="10" rx="1" fill="#0B0B0B" />
                    <rect x="84" y="74" width="8" height="8" rx="1" fill="#0B0B0B" />
                    <rect x="38" y="84" width="6" height="6" rx="1" fill="#0B0B0B" />
                    <rect x="50" y="82" width="10" height="10" rx="1" fill="#0B0B0B" />
                    <rect x="66" y="84" width="6" height="6" rx="1" fill="#0B0B0B" />
                    <rect x="78" y="86" width="8" height="8" rx="1" fill="#0B0B0B" />
                  </svg>
                </div>
                <div className="flex items-center gap-1 mt-1 text-[9px] font-black tracking-widest text-neutral-700">
                  <span className="text-[#097939]">U</span>
                  <span className="text-[#00529C]">P</span>
                  <span className="text-[#EA592A]">I</span>
                  <span className="text-[#EA592A] text-[8px]">▶</span>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-4 px-2 text-center bg-white rounded-xl border border-neutral-200/80 my-1">
                <div className="text-[10px] font-mono text-neutral-600 bg-neutral-100 rounded px-2 py-1 w-full truncate mb-2">
                  neuravolt.in/pay/inv_1040
                </div>
                <span className="text-[9px] font-bold text-[#FF5A1F]">Direct Instant Settlement Link</span>
              </div>
            )}

            {/* Payment Received Confirmation Card */}
            <div className="bg-white rounded-xl border border-neutral-200/80 p-2 flex items-center justify-between shadow-2xs">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0">
                  <Check className="w-3 h-3 stroke-[3]" />
                </div>
                <div>
                  <div className="text-[8px] font-bold uppercase tracking-wider text-neutral-500">Payment Received</div>
                  <div className="text-xs font-black text-neutral-900">₹12,000</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[8px] font-mono text-neutral-400">INV-2026-1040</div>
                <div className="text-[8px] text-neutral-400">2 min ago</div>
              </div>
            </div>
          </div>
        );

      case "team-chat":
        return (
          <div className="rounded-2xl border border-neutral-200/80 bg-white overflow-hidden shadow-xs flex h-[235px]">
            {/* Left Dark Sidebar */}
            <div className="bg-[#18181B] text-neutral-300 w-24 sm:w-28 p-2 flex flex-col justify-between shrink-0 select-none">
              <div>
                <div className="flex items-center gap-1.5 text-[9px] font-bold text-white mb-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#FF5A1F]" />
                  <span>NeuraChat</span>
                </div>
                <div className="bg-neutral-800/80 rounded px-1.5 py-0.5 flex items-center gap-1 text-[8px] text-neutral-400 mb-2">
                  <Search className="w-2.5 h-2.5" />
                  <span>Search...</span>
                </div>
                <div className="space-y-0.5">
                  <div className="bg-neutral-800 text-white font-medium rounded px-1.5 py-1 flex items-center justify-between text-[8px]">
                    <span className="truncate"># general</span>
                    <span className="bg-[#FF5A1F] text-white text-[7px] font-bold px-1 rounded-full">12</span>
                  </div>
                  <div className="text-neutral-400 px-1.5 py-0.5 text-[8px] truncate"># development</div>
                  <div className="text-neutral-400 px-1.5 py-0.5 text-[8px] truncate"># design</div>
                  <div className="text-neutral-400 px-1.5 py-0.5 text-[8px] truncate"># clients</div>
                  <div className="text-neutral-400 px-1.5 py-0.5 text-[8px] truncate"># announcements</div>
                </div>
              </div>
            </div>

            {/* Right Light Chat Pane */}
            <div className="flex-1 bg-white p-2 flex flex-col justify-between text-left">
              <div>
                <div className="flex items-center justify-between border-b border-neutral-100 pb-1 mb-1.5">
                  <span className="text-[9px] font-bold text-neutral-800"># general</span>
                  <div className="flex items-center gap-1 text-neutral-400">
                    <Video className="w-2.5 h-2.5" />
                    <Users className="w-2.5 h-2.5" />
                  </div>
                </div>

                {/* Messages */}
                <div className="space-y-1.5">
                  {/* Msg 1 */}
                  <div className="flex items-start gap-1.5">
                    <div className="w-4 h-4 rounded-full bg-orange-100 text-[#FF5A1F] text-[7px] font-bold flex items-center justify-center shrink-0">
                      A
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-1">
                        <span className="text-[8px] font-bold text-neutral-900">Ajay</span>
                        <span className="text-[7px] text-neutral-400 font-mono">10:24 AM</span>
                      </div>
                      <p className="text-[8px] text-neutral-600 leading-snug">The staging build is ready for review.</p>
                    </div>
                  </div>

                  {/* Msg 2 */}
                  <div className="flex items-start gap-1.5">
                    <div className="w-4 h-4 rounded-full bg-blue-100 text-blue-600 text-[7px] font-bold flex items-center justify-center shrink-0">
                      P
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-1">
                        <span className="text-[8px] font-bold text-neutral-900">Priya</span>
                        <span className="text-[7px] text-neutral-400 font-mono">10:26 AM</span>
                      </div>
                      <p className="text-[8px] text-neutral-600 leading-snug">Let's schedule a client call.</p>
                    </div>
                  </div>

                  {/* Join MeetPass Video Card */}
                  <div className="bg-blue-50/80 border border-blue-200/60 rounded-lg p-1.5 flex items-center gap-2">
                    <div className="w-6 h-6 rounded-md bg-blue-500 text-white flex items-center justify-center shrink-0 shadow-2xs">
                      <Video className="w-3 h-3" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[8px] font-bold text-blue-900">Join MeetPass</div>
                      <div className="text-[7px] text-blue-700">10:30 AM • 30 min</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Input row */}
              <div className="mt-1 bg-neutral-50 border border-neutral-200 rounded-md px-1.5 py-1 flex items-center justify-between text-[8px] text-neutral-400">
                <span>Type a message...</span>
                <div className="flex items-center gap-1 text-neutral-400">
                  <Paperclip className="w-2.5 h-2.5" />
                </div>
              </div>
            </div>
          </div>
        );

      case "workforce-hrm":
        return (
          <div className="rounded-2xl bg-neutral-50/70 border border-neutral-200/80 p-3 flex flex-col justify-between text-left h-[235px]">
            {/* Map Geofence Visual */}
            <div className="relative rounded-xl overflow-hidden h-20 bg-[#E8F0E6] border border-neutral-200/70 flex items-center justify-center">
              {/* Grid & Map streets lines */}
              <div className="absolute inset-0 opacity-40">
                <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <pattern id="mapGrid" width="16" height="16" patternUnits="userSpaceOnUse">
                      <path d="M 16 0 L 0 0 0 16" fill="none" stroke="#9BB098" strokeWidth="0.5" />
                    </pattern>
                  </defs>
                  <rect width="100%" height="100%" fill="url(#mapGrid)" />
                  <path d="M -20,40 Q 60,10 160,30 T 240,60" fill="none" stroke="#CBDAC8" strokeWidth="3" />
                  <path d="M 40,-10 L 120,90" fill="none" stroke="#CBDAC8" strokeWidth="2.5" />
                </svg>
              </div>

              {/* Green Geofence Circle Radius */}
              <div className="absolute w-16 h-16 rounded-full bg-emerald-500/15 border-2 border-dashed border-emerald-500/60 flex items-center justify-center animate-pulse" />

              {/* Map Pin */}
              <div className="relative z-10 flex flex-col items-center">
                <MapPin className="w-5 h-5 text-rose-500 fill-rose-500 drop-shadow-xs" />
              </div>

              {/* Floating Badge */}
              <div className="absolute top-1.5 right-1.5 z-20 bg-white/95 backdrop-blur-xs rounded-lg px-2 py-0.5 shadow-2xs border border-neutral-200/80 text-left">
                <div className="text-[8px] font-bold text-neutral-900 leading-tight">Office Branch</div>
                <div className="text-[7px] text-emerald-600 font-medium flex items-center gap-1">
                  <span className="w-1 h-1 rounded-full bg-emerald-500" />
                  <span>Checked In • 9:12 AM</span>
                </div>
              </div>
            </div>

            {/* Check-in Employee List */}
            <div className="space-y-1.5 pt-1">
              <div className="flex items-center justify-between text-[9px] bg-white rounded-lg p-1 border border-neutral-200/60 shadow-2xs">
                <div className="flex items-center gap-1.5">
                  <div className="w-4 h-4 rounded-full bg-neutral-200 text-neutral-700 text-[7px] font-bold flex items-center justify-center shrink-0">AT</div>
                  <div>
                    <span className="font-bold text-neutral-800 text-[8px] block leading-tight">Aditya Tiwari</span>
                    <span className="text-[7px] text-neutral-400">Checked In • 9:12 AM</span>
                  </div>
                </div>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0 mr-1" />
              </div>

              <div className="flex items-center justify-between text-[9px] bg-white rounded-lg p-1 border border-neutral-200/60 shadow-2xs">
                <div className="flex items-center gap-1.5">
                  <div className="w-4 h-4 rounded-full bg-neutral-200 text-neutral-700 text-[7px] font-bold flex items-center justify-center shrink-0">RS</div>
                  <div>
                    <span className="font-bold text-neutral-800 text-[8px] block leading-tight">Rohit Sharma</span>
                    <span className="text-[7px] text-neutral-400">Remote • 9:05 AM</span>
                  </div>
                </div>
                <span className="w-1.5 h-1.5 rounded-full bg-[#FF5A1F] shrink-0 mr-1" />
              </div>

              <div className="flex items-center justify-between text-[9px] bg-white rounded-lg p-1 border border-neutral-200/60 shadow-2xs">
                <div className="flex items-center gap-1.5">
                  <div className="w-4 h-4 rounded-full bg-neutral-200 text-neutral-700 text-[7px] font-bold flex items-center justify-center shrink-0">NV</div>
                  <div>
                    <span className="font-bold text-neutral-800 text-[8px] block leading-tight">Neha Verma</span>
                    <span className="text-[7px] text-neutral-400">Checked In • 9:30 AM</span>
                  </div>
                </div>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0 mr-1" />
              </div>
            </div>
          </div>
        );

      case "client-portal":
        return (
          <div className="rounded-2xl border border-neutral-200/80 bg-white p-2.5 shadow-xs flex flex-col justify-between text-left h-[235px]">
            {/* Tenant Top Bar */}
            <div className="flex items-center justify-between border-b border-neutral-100 pb-1 mb-1">
              <div className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-xs bg-[#FF5A1F] flex items-center justify-center text-white text-[6px] font-bold">▲</span>
                <span className="text-[9px] font-black text-neutral-900 tracking-tight">ACME Corp</span>
                <ChevronDown className="w-2.5 h-2.5 text-neutral-400" />
              </div>
              <span className="text-[7px] text-neutral-400">›</span>
            </div>

            {/* Split Body */}
            <div className="flex gap-2 flex-1 min-h-0">
              {/* Mini Sidebar */}
              <div className="w-16 space-y-0.5 shrink-0 border-r border-neutral-100 pr-1 select-none">
                <button
                  type="button"
                  onClick={() => setPortalTab("dashboard")}
                  className={`w-full text-left rounded px-1.5 py-0.5 text-[7px] flex items-center gap-1 transition-all ${
                    portalTab === "dashboard"
                      ? "bg-orange-50 text-[#FF5A1F] font-bold"
                      : "text-neutral-400 hover:text-neutral-700"
                  }`}
                >
                  <Laptop className="w-2 h-2" />
                  <span>Dashboard</span>
                </button>
                <button
                  type="button"
                  onClick={() => setPortalTab("projects")}
                  className={`w-full text-left rounded px-1.5 py-0.5 text-[7px] flex items-center gap-1 transition-all ${
                    portalTab === "projects"
                      ? "bg-orange-50 text-[#FF5A1F] font-bold"
                      : "text-neutral-400 hover:text-neutral-700"
                  }`}
                >
                  <Boxes className="w-2 h-2" />
                  <span>Projects</span>
                </button>
                <button
                  type="button"
                  onClick={() => setPortalTab("tickets")}
                  className={`w-full text-left rounded px-1.5 py-0.5 text-[7px] flex items-center gap-1 transition-all ${
                    portalTab === "tickets"
                      ? "bg-orange-50 text-[#FF5A1F] font-bold"
                      : "text-neutral-400 hover:text-neutral-700"
                  }`}
                >
                  <FileText className="w-2 h-2" />
                  <span>Tickets</span>
                </button>
                <div className="text-neutral-400 px-1.5 py-0.5 text-[7px] flex items-center gap-1">
                  <CreditCard className="w-2 h-2" />
                  <span>Invoices</span>
                </div>
                <div className="text-neutral-400 px-1.5 py-0.5 text-[7px] flex items-center gap-1">
                  <Users className="w-2 h-2" />
                  <span>Team</span>
                </div>
              </div>

              {/* Mini Dashboard Content */}
              <div className="flex-1 min-w-0 flex flex-col justify-between">
                {/* Project Progress */}
                <div className="bg-neutral-50/80 rounded-lg p-1.5 border border-neutral-200/50">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[8px] font-bold text-neutral-700">Project Progress</span>
                    <span className="text-[8px] font-black text-neutral-900">75%</span>
                  </div>
                  <div className="w-full bg-neutral-200 rounded-full h-1.5 overflow-hidden mb-1">
                    <div className="bg-[#FF5A1F] h-full rounded-full transition-all duration-500" style={{ width: "75%" }} />
                  </div>
                  <div className="text-[7px] text-neutral-400">3 / 4 milestones completed</div>
                </div>

                {/* Recent Tickets */}
                <div>
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-[8px] font-bold text-neutral-700">Recent Tickets</span>
                    <span className="text-[7px] text-neutral-400 font-medium">View all</span>
                  </div>
                  <div className="space-y-0.5">
                    <div className="flex items-center justify-between text-[7px] py-0.5">
                      <span className="text-neutral-700 font-medium flex items-center gap-1 truncate">
                        <span className="w-1 h-1 rounded-full bg-blue-500" />
                        <span className="font-mono">#1042</span>
                      </span>
                      <span className="px-1 py-0.2 rounded bg-blue-50 text-blue-600 font-medium text-[6px]">In Progress</span>
                      <span className="text-[6px] text-neutral-400 font-mono">2h ago</span>
                    </div>
                    <div className="flex items-center justify-between text-[7px] py-0.5">
                      <span className="text-neutral-700 font-medium flex items-center gap-1 truncate">
                        <span className="w-1 h-1 rounded-full bg-emerald-500" />
                        <span className="font-mono">#1041</span>
                      </span>
                      <span className="px-1 py-0.2 rounded bg-emerald-50 text-emerald-600 font-medium text-[6px]">Resolved</span>
                      <span className="text-[6px] text-neutral-400 font-mono">1d ago</span>
                    </div>
                    <div className="flex items-center justify-between text-[7px] py-0.5">
                      <span className="text-neutral-700 font-medium flex items-center gap-1 truncate">
                        <span className="w-1 h-1 rounded-full bg-amber-500" />
                        <span className="font-mono">#1039</span>
                      </span>
                      <span className="px-1 py-0.2 rounded bg-amber-50 text-amber-600 font-medium text-[6px]">Pending</span>
                      <span className="text-[6px] text-neutral-400 font-mono">2d ago</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Layout>
      <Seo
        title="nORBIT v2.0: The Sovereign Enterprise Operating System | NeuraVolt"
        description="One Platform. Your Entire Business. Replace 8+ fragmented SaaS tools with one sovereign in-house stack: Guardian SRE, Staging DevOps, NeuraCollect UPI payments, team chat, HRMS, and client portals."
        keywords="nORBIT, enterprise operating system, sovereign enterprise OS, NeuraVolt, replace saas sprawl, self hosted ERP, Guardian SRE, NeuraCollect, DPDP-2023, client portal, staging devops"
        path="/norbit"
        jsonLd={jsonLd}
      />

      {/* ──────────────────────────────────────────────────────────────────── */}
      {/* 1. HERO SECTION (ABOVE THE FOLD)                                     */}
      {/* ──────────────────────────────────────────────────────────────────── */}
      <section
        id="overview"
        className="scroll-mt-24 relative pt-32 pb-16 sm:pb-24 px-4 sm:px-8 bg-gradient-to-b from-[#FAF9F6] via-white to-[#FAF9F6] text-[#0B0B0B] overflow-hidden border-b border-black/[0.06]"
      >
        {/* Soft background ambient glow */}
        <div className="absolute top-1/4 right-0 w-[650px] h-[650px] bg-gradient-to-br from-[#FF5A1F]/15 via-orange-300/10 to-transparent blur-[140px] rounded-full pointer-events-none" />
        <div className="absolute top-10 left-10 w-[400px] h-[400px] bg-blue-500/5 blur-[120px] rounded-full pointer-events-none" />

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            {/* Left Content */}
            <div className="lg:col-span-6 text-left">
              {/* Top Pill Badge */}
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white border border-neutral-200/90 shadow-2xs mb-6 backdrop-blur-md">
                <span className="w-2 h-2 rounded-full bg-[#FF5A1F]" />
                <span className="text-xs font-mono font-medium tracking-wide text-neutral-800">
                  NORBIT v2.0 <span className="text-neutral-400 mx-1">|</span> The Sovereign Enterprise Operating System
                </span>
              </div>

              {/* Logo Emblem & Tagline */}
              <div className="flex items-center gap-3.5 mb-6">
                <img
                  src="/brand/norbit-logo.png"
                  alt="NORBIT"
                  className="h-10 sm:h-12 w-auto object-contain select-none"
                />
                <div className="border-l border-neutral-300 pl-3 leading-none">
                  <div className="text-[10px] font-mono font-bold tracking-widest text-neutral-500 uppercase">
                    ONE PLATFORM.
                  </div>
                  <div className="text-[10px] font-mono font-bold tracking-widest text-neutral-500 uppercase mt-0.5">
                    YOUR ENTIRE BUSINESS.
                  </div>
                </div>
              </div>

              {/* Main Headline */}
              <h1 className="text-4xl sm:text-6xl lg:text-[68px] font-display font-extrabold tracking-tight text-[#0B0B0B] leading-[1.04] mb-6">
                One Platform.{" "}
                <span className="text-[#FF5A1F] block">
                  Your Entire Business.
                </span>
              </h1>

              {/* Punchy Sub-headline */}
              <p className="text-neutral-600 text-base sm:text-lg leading-relaxed mb-8 max-w-xl font-normal">
                Replace fragmented SaaS tools with one sovereign enterprise stack. From autonomous security and DevOps to payments, HRMS, and client portals — built, owned and controlled by you.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <button
                  onClick={() => openConsultationModal("Request Live Demo - nORBIT v2.0")}
                  className="px-7 py-3.5 rounded-full bg-[#FF5A1F] hover:bg-[#ff6d38] text-white font-bold text-sm transition-all duration-300 shadow-lg shadow-[#FF5A1F]/25 flex items-center gap-2 group cursor-pointer"
                >
                  <span>Request Live Demo</span>
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>

                <a
                  href="#core-engines"
                  className="px-6 py-3.5 rounded-full bg-white hover:bg-neutral-50 text-neutral-800 font-semibold text-sm transition-all duration-300 border border-neutral-300 shadow-2xs flex items-center gap-2 cursor-pointer"
                >
                  <span>Explore Core Engines</span>
                  <ArrowRight className="w-4 h-4 text-neutral-400" />
                </a>
              </div>
            </div>

            {/* Right Visual: 3D Perspective Device Mockup */}
            <div className="lg:col-span-6 relative mt-8 lg:mt-0">
              {/* Soft Ambient Glow */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[460px] h-[460px] bg-gradient-to-tr from-[#FF5A1F]/20 via-orange-300/15 to-transparent blur-3xl rounded-full pointer-events-none" />

              {/* Device Mockup with 3D Perspective Tilt & Real Dashboard Screenshot */}
              <div className="relative z-10 [perspective:1400px]">
                <div className="rounded-3xl bg-white p-2.5 sm:p-3 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.18)] border border-neutral-200/90 transition-transform duration-500 hover:scale-[1.01] sm:[transform:rotateY(-7deg)_rotateX(4deg)_rotateZ(0.5deg)]">
                  {/* Device Window Header: Window Controls + View Toggle */}
                  <div className="flex items-center justify-between px-3 py-2 border-b border-neutral-100 mb-2">
                    <div className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
                      <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                      <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                    </div>

                    {/* Screenshot View Toggle */}
                    <div className="flex items-center gap-1 bg-neutral-100 p-0.5 rounded-full text-[10px] font-medium text-neutral-600">
                      <button
                        type="button"
                        onClick={() => setDashboardView(1)}
                        className={`px-2.5 py-0.5 rounded-full transition-all cursor-pointer ${dashboardView === 1
                            ? "bg-white text-neutral-900 shadow-2xs font-semibold"
                            : "hover:text-neutral-900"
                          }`}
                      >
                        Command
                      </button>
                      <button
                        type="button"
                        onClick={() => setDashboardView(2)}
                        className={`px-2.5 py-0.5 rounded-full transition-all cursor-pointer ${dashboardView === 2
                            ? "bg-white text-neutral-900 shadow-2xs font-semibold"
                            : "hover:text-neutral-900"
                          }`}
                      >
                        Modules
                      </button>
                    </div>
                  </div>

                  {/* Dashboard Screenshot Preview */}
                  <div className="relative rounded-2xl overflow-hidden bg-white border border-neutral-100">
                    <img
                      src={
                        dashboardView === 1
                          ? "/brand/norbit-dashboard-preview.png"
                          : "/brand/norbit-dashboard-preview-2.png"
                      }
                      alt="nORBIT Sovereign Enterprise OS Dashboard"
                      className="w-full h-auto object-cover select-none transition-opacity duration-300"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Under Hero: 4-Column Feature / Value Proposition Bar (Matching Image 1) */}
          <div className="mt-16 pt-10 border-t border-black/[0.08]">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 text-left">
              {/* Feature 1 */}
              <div className="flex items-start gap-3.5">
                <div className="w-10 h-10 rounded-xl bg-orange-50 border border-orange-200/60 text-[#FF5A1F] grid place-items-center shrink-0">
                  <Shield className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-sm font-bold text-neutral-900">Enterprise Grade Security</div>
                  <div className="text-xs text-neutral-500 mt-0.5 leading-relaxed">
                    Autonomous threat detection & response
                  </div>
                </div>
              </div>

              {/* Feature 2 */}
              <div className="flex items-start gap-3.5">
                <div className="w-10 h-10 rounded-xl bg-orange-50 border border-orange-200/60 text-[#FF5A1F] grid place-items-center shrink-0">
                  <Boxes className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-sm font-bold text-neutral-900">Unified Business Stack</div>
                  <div className="text-xs text-neutral-500 mt-0.5 leading-relaxed">
                    Replace 8+ fragmented SaaS tools
                  </div>
                </div>
              </div>

              {/* Feature 3 */}
              <div className="flex items-start gap-3.5">
                <div className="w-10 h-10 rounded-xl bg-orange-50 border border-orange-200/60 text-[#FF5A1F] grid place-items-center shrink-0">
                  <CreditCard className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-sm font-bold text-neutral-900">Direct Payment Integrations</div>
                  <div className="text-xs text-neutral-500 mt-0.5 leading-relaxed">
                    UPI, Cards & Bank APIs
                  </div>
                </div>
              </div>

              {/* Feature 4 */}
              <div className="flex items-start gap-3.5">
                <div className="w-10 h-10 rounded-xl bg-orange-50 border border-orange-200/60 text-[#FF5A1F] grid place-items-center shrink-0">
                  <Cloud className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-sm font-bold text-neutral-900">100% Data Sovereignty</div>
                  <div className="text-xs text-neutral-500 mt-0.5 leading-relaxed">
                    Your infrastructure. Your control.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────────────── */}
      {/* 2. SAAS CONSOLIDATION (COMPACT ENTERPRISE OVERVIEW)                  */}
      {/* ──────────────────────────────────────────────────────────────────── */}
      <section
        id="saas-disruption"
        className="scroll-mt-24 py-16 sm:py-20 px-4 sm:px-8 bg-white border-b border-black/[0.06]"
      >
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-orange-50 border border-orange-200/70 text-[#FF5A1F] text-xs font-mono font-bold uppercase tracking-wider mb-3">
              <span className="w-2 h-2 rounded-full bg-[#FF5A1F]" />
              <span>CONSOLIDATED OPERATIONS</span>
            </div>
            <h2 className="text-3xl sm:text-5xl font-display font-black text-[#0B0B0B] tracking-tight mb-3">
              Less Tool Sprawl. More Control.
            </h2>
            <p className="text-neutral-500 text-sm sm:text-base leading-relaxed max-w-2xl mx-auto">
              Replace 10+ disconnected tools with one unified platform for engineering, billing, workforce and client operations — with built-in automations.
            </p>
          </div>

          {/* Main Comparison Grid: Left (Traditional Stack) ➔ Center (Bridge) ➔ Right (The nORBIT Way) */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-stretch relative">
            
            {/* ──────────────────────────────────────────────────────────── */}
            {/* LEFT CARD: TRADITIONAL STACK (FRAGMENTED)                    */}
            {/* ──────────────────────────────────────────────────────────── */}
            <div className="lg:col-span-5 rounded-3xl bg-[#FBFBFC] border border-neutral-200/80 p-6 sm:p-7 flex flex-col justify-between shadow-2xs">
              <div>
                {/* Header Row */}
                <div className="flex items-center justify-between pb-3 mb-4 border-b border-neutral-100">
                  <span className="text-xs font-mono font-bold uppercase tracking-wider text-neutral-400">
                    TRADITIONAL STACK
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold text-rose-600 bg-rose-50 border border-rose-200/60">
                    <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
                    <span>Fragmented</span>
                  </span>
                </div>

                {/* Title & Subtitle */}
                <h3 className="text-xl font-bold text-neutral-900 mb-1 leading-tight">
                  Disconnected SaaS Point Solutions
                </h3>
                <p className="text-xs text-neutral-500 leading-relaxed mb-6">
                  Multiple vendor subscriptions, scattered data, separate logins and manual handoffs across your business.
                </p>

                {/* 2-Column SaaS Logos Grid */}
                <div className="grid grid-cols-2 gap-3 mb-6">
                  
                  {/* 1. Jira */}
                  <div className="flex items-center gap-3 p-2 rounded-xl bg-white border border-neutral-200/70 shadow-2xs">
                    <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                      <svg className="w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M11.53 2c0 2.4-1.97 4.35-4.4 4.35H2.8C2.36 6.35 2 6.7 2 7.15v4.32c0 2.4 1.97 4.35 4.4 4.35h4.33c.44 0 .8-.36.8-.8V2.8c0-.44-.36-.8-.8-.8h.8zm9.67 9.87c0 2.4-1.97 4.35-4.4 4.35h-4.33c-.44 0-.8.36-.8.8v8.18c0 .44.36.8.8.8h.8c2.4 0 4.35-1.97 4.35-4.4v-4.33c0-.44.36-.8.8-.8h4.33c.44 0 .8-.36.8-.8v-4.32c0-.44-.36-.8-.8-.8h-.75z" />
                      </svg>
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-bold text-neutral-900 truncate">Jira</div>
                      <div className="text-[10px] text-neutral-400 truncate">Projects & Tasks</div>
                    </div>
                  </div>

                  {/* 2. GitHub */}
                  <div className="flex items-center gap-3 p-2 rounded-xl bg-white border border-neutral-200/70 shadow-2xs">
                    <div className="w-8 h-8 rounded-lg bg-neutral-900 text-white flex items-center justify-center shrink-0">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.53 1.032 1.53 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" />
                      </svg>
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-bold text-neutral-900 truncate">GitHub</div>
                      <div className="text-[10px] text-neutral-400 truncate">Code & PRs</div>
                    </div>
                  </div>

                  {/* 3. Slack */}
                  <div className="flex items-center gap-3 p-2 rounded-xl bg-white border border-neutral-200/70 shadow-2xs">
                    <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                      <span className="text-sm font-black text-emerald-600">#</span>
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-bold text-neutral-900 truncate">Slack</div>
                      <div className="text-[10px] text-neutral-400 truncate">Team Chat</div>
                    </div>
                  </div>

                  {/* 4. Datadog */}
                  <div className="flex items-center gap-3 p-2 rounded-xl bg-white border border-neutral-200/70 shadow-2xs">
                    <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center shrink-0">
                      <span className="text-xs font-bold text-purple-600 font-mono">DD</span>
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-bold text-neutral-900 truncate">Datadog</div>
                      <div className="text-[10px] text-neutral-400 truncate">Infra Monitoring</div>
                    </div>
                  </div>

                  {/* 5. Google Workspace */}
                  <div className="flex items-center gap-3 p-2 rounded-xl bg-white border border-neutral-200/70 shadow-2xs">
                    <div className="w-8 h-8 rounded-lg bg-rose-50 flex items-center justify-center shrink-0">
                      <span className="text-xs font-bold text-rose-600 font-mono">M</span>
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-bold text-neutral-900 truncate">Google Workspace</div>
                      <div className="text-[10px] text-neutral-400 truncate">Email & Calendar</div>
                    </div>
                  </div>

                  {/* 6. HubSpot */}
                  <div className="flex items-center gap-3 p-2 rounded-xl bg-white border border-neutral-200/70 shadow-2xs">
                    <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center shrink-0">
                      <span className="text-xs font-bold text-orange-600 font-mono">HS</span>
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-bold text-neutral-900 truncate">HubSpot</div>
                      <div className="text-[10px] text-neutral-400 truncate">CRM & Sales</div>
                    </div>
                  </div>

                  {/* 7. ServiceNow */}
                  <div className="flex items-center gap-3 p-2 rounded-xl bg-white border border-neutral-200/70 shadow-2xs">
                    <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center shrink-0">
                      <span className="text-[10px] font-bold text-teal-700 font-mono">NOW</span>
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-bold text-neutral-900 truncate">ServiceNow</div>
                      <div className="text-[10px] text-neutral-400 truncate">ITSM & Ops</div>
                    </div>
                  </div>

                  {/* 8. BambooHR */}
                  <div className="flex items-center gap-3 p-2 rounded-xl bg-white border border-neutral-200/70 shadow-2xs">
                    <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                      <span className="text-xs font-bold text-emerald-600">🌿</span>
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-bold text-neutral-900 truncate">BambooHR</div>
                      <div className="text-[10px] text-neutral-400 truncate">HRM & Leaves</div>
                    </div>
                  </div>

                  {/* 9. Stripe */}
                  <div className="flex items-center gap-3 p-2 rounded-xl bg-white border border-neutral-200/70 shadow-2xs">
                    <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
                      <span className="text-xs font-bold text-indigo-600 font-mono">S</span>
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-bold text-neutral-900 truncate">Stripe</div>
                      <div className="text-[10px] text-neutral-400 truncate">Payments & Billing</div>
                    </div>
                  </div>

                  {/* 10. ZKTeco */}
                  <div className="flex items-center gap-3 p-2 rounded-xl bg-white border border-neutral-200/70 shadow-2xs">
                    <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center shrink-0">
                      <span className="text-[10px] font-bold text-green-700 font-mono">ZK</span>
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-bold text-neutral-900 truncate">ZKTeco</div>
                      <div className="text-[10px] text-neutral-400 truncate">Biometric Clock</div>
                    </div>
                  </div>

                  {/* 11. QuickBooks */}
                  <div className="flex items-center gap-3 p-2 rounded-xl bg-white border border-neutral-200/70 shadow-2xs">
                    <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                      <span className="text-[10px] font-bold text-emerald-700 font-mono">qb</span>
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-bold text-neutral-900 truncate">QuickBooks</div>
                      <div className="text-[10px] text-neutral-400 truncate">Accounting</div>
                    </div>
                  </div>

                  {/* 12. Asana */}
                  <div className="flex items-center gap-3 p-2 rounded-xl bg-white border border-neutral-200/70 shadow-2xs">
                    <div className="w-8 h-8 rounded-lg bg-rose-50 flex items-center justify-center shrink-0">
                      <span className="text-xs font-bold text-rose-500 font-mono">∴</span>
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-bold text-neutral-900 truncate">Asana</div>
                      <div className="text-[10px] text-neutral-400 truncate">Work Management</div>
                    </div>
                  </div>

                </div>
              </div>

              {/* Bottom 3 Pain-Point Pills */}
              <div className="grid grid-cols-3 gap-2 pt-4 border-t border-neutral-200/80">
                <div className="p-2 rounded-xl bg-white border border-neutral-200/60 text-left">
                  <CreditCard className="w-4 h-4 text-[#FF5A1F] mb-1" />
                  <div className="text-[11px] font-bold text-neutral-900 leading-tight">Multiple Subscriptions</div>
                  <div className="text-[10px] text-neutral-400 mt-0.5">Higher recurring costs</div>
                </div>

                <div className="p-2 rounded-xl bg-white border border-neutral-200/60 text-left">
                  <Clock className="w-4 h-4 text-[#FF5A1F] mb-1" />
                  <div className="text-[11px] font-bold text-neutral-900 leading-tight">Manual Handoffs</div>
                  <div className="text-[10px] text-neutral-400 mt-0.5">Repetitive data entry</div>
                </div>

                <div className="p-2 rounded-xl bg-white border border-neutral-200/60 text-left">
                  <Activity className="w-4 h-4 text-[#FF5A1F] mb-1" />
                  <div className="text-[11px] font-bold text-neutral-900 leading-tight">Scattered Data</div>
                  <div className="text-[10px] text-neutral-400 mt-0.5">No unified view</div>
                </div>
              </div>

            </div>

            {/* ──────────────────────────────────────────────────────────── */}
            {/* CENTER TRANSFORMATION CONNECTOR                             */}
            {/* ──────────────────────────────────────────────────────────── */}
            <div className="lg:col-span-2 hidden lg:flex flex-col items-center justify-center text-center relative py-6">
              
              {/* Top Labels */}
              <div className="mb-4 space-y-1 relative z-10">
                <div className="text-[10px] font-mono font-bold tracking-wider text-neutral-400 uppercase">AUTOMATIONS</div>
                <div className="text-[10px] font-mono font-bold tracking-wider text-neutral-400 uppercase">INTEGRATIONS</div>
                <div className="text-[10px] font-mono font-bold tracking-wider text-neutral-400 uppercase">UNIFIED DATA</div>
              </div>

              {/* Central Glowing Arrow Button */}
              <div className="w-12 h-12 rounded-full bg-white border-2 border-orange-200 shadow-md flex items-center justify-center text-[#FF5A1F] text-lg font-bold my-2 relative z-10">
                ➔
              </div>

              {/* Bottom Labels */}
              <div className="mt-4 space-y-1 relative z-10">
                <div className="text-[10px] font-mono font-bold tracking-wider text-neutral-400 uppercase">ONE PLATFORM</div>
                <div className="text-[10px] font-mono font-bold tracking-wider text-neutral-400 uppercase">ONE LOGIN</div>
                <div className="text-[10px] font-mono font-bold tracking-wider text-neutral-400 uppercase">FULL CONTROL</div>
              </div>

            </div>

            {/* ──────────────────────────────────────────────────────────── */}
            {/* RIGHT CARD: THE nORBIT WAY (UNIFIED PLATFORM)                */}
            {/* ──────────────────────────────────────────────────────────── */}
            <div className="lg:col-span-5 rounded-3xl bg-white border-2 border-orange-400/90 p-6 sm:p-7 flex flex-col justify-between shadow-xs">
              <div>
                {/* Header Row */}
                <div className="flex items-center justify-between pb-3 mb-4 border-b border-neutral-100">
                  <span className="text-xs font-mono font-bold uppercase tracking-wider text-[#FF5A1F]">
                    THE nORBIT WAY
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200/60">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    <span>Unified Platform</span>
                  </span>
                </div>

                {/* Title & Subtitle */}
                <h3 className="text-xl font-bold text-neutral-900 mb-1 leading-tight">
                  One Connected Enterprise Platform
                </h3>
                <p className="text-xs text-neutral-500 leading-relaxed mb-6">
                  A single workspace for your entire business — from sales and delivery to payments, workforce and infrastructure operations.
                </p>

                {/* 6 Integrated Module Rows */}
                <div className="space-y-2 mb-6">
                  
                  {/* 1. CRM & Client Operations */}
                  <div 
                    onClick={() => setSelectedEngine(engines[0])}
                    className="p-2.5 rounded-2xl bg-neutral-50/70 border border-neutral-200/70 hover:border-orange-300 hover:bg-orange-50/20 transition-all flex items-center justify-between gap-3 cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-xl bg-orange-100/70 text-[#FF5A1F] flex items-center justify-center shrink-0">
                        <Users className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs font-bold text-neutral-900 truncate">CRM & Client Operations</div>
                        <div className="text-[10px] text-neutral-400 truncate">Leads, quotations, client onboarding & support tickets</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <div className="w-16 h-8 rounded-lg bg-white border border-neutral-200/80 p-1 flex flex-col justify-between shadow-2xs">
                        <div className="flex items-center justify-between">
                          <span className="w-6 h-1.5 bg-neutral-200 rounded-full" />
                          <span className="w-2 h-1.5 bg-emerald-500 rounded-full" />
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="w-3 h-1 bg-orange-400 rounded-full" />
                          <span className="w-5 h-1 bg-neutral-200 rounded-full" />
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-neutral-400" />
                    </div>
                  </div>

                  {/* 2. Projects & DevOps */}
                  <div 
                    onClick={() => setSelectedEngine(engines[1])}
                    className="p-2.5 rounded-2xl bg-neutral-50/70 border border-neutral-200/70 hover:border-orange-300 hover:bg-orange-50/20 transition-all flex items-center justify-between gap-3 cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-xl bg-orange-100/70 text-[#FF5A1F] flex items-center justify-center shrink-0 font-mono font-bold text-xs">
                        <Code2 className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs font-bold text-neutral-900 truncate">Projects & DevOps</div>
                        <div className="text-[10px] text-neutral-400 truncate">Tasks, GitHub PR sync, time tracking & AMC management</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <div className="w-16 h-8 rounded-lg bg-white border border-neutral-200/80 p-1 flex flex-col justify-between shadow-2xs">
                        <div className="flex items-center justify-between">
                          <span className="w-5 h-1.5 bg-blue-500 rounded-full" />
                          <span className="w-3 h-1.5 bg-neutral-200 rounded-full" />
                        </div>
                        <div className="w-full h-1 bg-neutral-100 rounded-full overflow-hidden">
                          <div className="w-3/4 h-full bg-emerald-500" />
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-neutral-400" />
                    </div>
                  </div>

                  {/* 3. Payments & Billing */}
                  <div 
                    onClick={() => setSelectedEngine(engines[2])}
                    className="p-2.5 rounded-2xl bg-neutral-50/70 border border-neutral-200/70 hover:border-orange-300 hover:bg-orange-50/20 transition-all flex items-center justify-between gap-3 cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-xl bg-orange-100/70 text-[#FF5A1F] flex items-center justify-center shrink-0">
                        <CreditCard className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs font-bold text-neutral-900 truncate">Payments & Billing</div>
                        <div className="text-[10px] text-neutral-400 truncate">NeuraCollect, UPI, Razorpay/Cashfree & reconciliation</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <div className="w-16 h-8 rounded-lg bg-white border border-neutral-200/80 p-1 flex flex-col justify-between shadow-2xs">
                        <div className="text-[7px] font-mono text-neutral-500 font-bold">₹1,77,000</div>
                        <span className="px-1 py-0.2 bg-emerald-50 text-emerald-700 text-[6px] font-bold rounded">0% FEE</span>
                      </div>
                      <ChevronRight className="w-4 h-4 text-neutral-400" />
                    </div>
                  </div>

                  {/* 4. HRM & Workforce */}
                  <div 
                    onClick={() => setSelectedEngine(engines[3])}
                    className="p-2.5 rounded-2xl bg-neutral-50/70 border border-neutral-200/70 hover:border-orange-300 hover:bg-orange-50/20 transition-all flex items-center justify-between gap-3 cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-xl bg-orange-100/70 text-[#FF5A1F] flex items-center justify-center shrink-0">
                        <Fingerprint className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs font-bold text-neutral-900 truncate">HRM & Workforce</div>
                        <div className="text-[10px] text-neutral-400 truncate">Attendance, biometric, leaves & role-based access</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <div className="w-16 h-8 rounded-lg bg-white border border-neutral-200/80 p-1 flex items-center justify-between shadow-2xs">
                        <div className="w-4 h-4 rounded-full bg-neutral-200 flex items-center justify-center text-[7px] font-bold">AD</div>
                        <div className="text-[7px] font-mono text-emerald-600 font-bold">PRESENT</div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-neutral-400" />
                    </div>
                  </div>

                  {/* 5. Team Collaboration */}
                  <div 
                    onClick={() => setSelectedEngine(engines[4])}
                    className="p-2.5 rounded-2xl bg-neutral-50/70 border border-neutral-200/70 hover:border-orange-300 hover:bg-orange-50/20 transition-all flex items-center justify-between gap-3 cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-xl bg-orange-100/70 text-[#FF5A1F] flex items-center justify-center shrink-0">
                        <Mail className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs font-bold text-neutral-900 truncate">Team Collaboration</div>
                        <div className="text-[10px] text-neutral-400 truncate">Native mailbox, chat, MeetPass meetings & DLP</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <div className="w-16 h-8 rounded-lg bg-white border border-neutral-200/80 p-1 flex flex-col justify-between shadow-2xs">
                        <div className="w-7 h-1.5 bg-neutral-200 rounded-full" />
                        <div className="flex items-center gap-1">
                          <span className="w-2 h-2 rounded-full bg-blue-500" />
                          <span className="w-5 h-1 bg-neutral-300 rounded-full" />
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-neutral-400" />
                    </div>
                  </div>

                  {/* 6. Infrastructure & Security */}
                  <div 
                    onClick={() => setSelectedEngine(engines[5])}
                    className="p-2.5 rounded-2xl bg-neutral-50/70 border border-neutral-200/70 hover:border-orange-300 hover:bg-orange-50/20 transition-all flex items-center justify-between gap-3 cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-xl bg-orange-100/70 text-[#FF5A1F] flex items-center justify-center shrink-0">
                        <Shield className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs font-bold text-neutral-900 truncate">Infrastructure & Security</div>
                        <div className="text-[10px] text-neutral-400 truncate">Guardian SRE, host monitoring & defense</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <div className="w-16 h-8 rounded-lg bg-white border border-neutral-200/80 p-1 flex items-center justify-between shadow-2xs">
                        <span className="text-[7px] font-mono text-emerald-600 font-bold">99.9%</span>
                        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping" />
                      </div>
                      <ChevronRight className="w-4 h-4 text-neutral-400" />
                    </div>
                  </div>

                </div>
              </div>

              {/* Bottom 3 Value Props */}
              <div className="grid grid-cols-3 gap-2 pt-4 border-t border-neutral-100">
                <div className="p-2 rounded-xl bg-orange-50/40 border border-orange-200/60 text-left">
                  <Database className="w-4 h-4 text-[#FF5A1F] mb-1" />
                  <div className="text-[11px] font-bold text-neutral-900 leading-tight">Unified Data</div>
                  <div className="text-[10px] text-neutral-500 mt-0.5">Single source of truth</div>
                </div>

                <div className="p-2 rounded-xl bg-orange-50/40 border border-orange-200/60 text-left">
                  <Zap className="w-4 h-4 text-[#FF5A1F] mb-1" />
                  <div className="text-[11px] font-bold text-neutral-900 leading-tight">Automations</div>
                  <div className="text-[10px] text-neutral-500 mt-0.5">Reduce manual work</div>
                </div>

                <div className="p-2 rounded-xl bg-orange-50/40 border border-orange-200/60 text-left">
                  <Server className="w-4 h-4 text-[#FF5A1F] mb-1" />
                  <div className="text-[11px] font-bold text-neutral-900 leading-tight">Deploy Anywhere</div>
                  <div className="text-[10px] text-neutral-500 mt-0.5">Hetzner, AWS or bare metal</div>
                </div>
              </div>

            </div>

          </div>
        </div>
      </section>


      {/* ──────────────────────────────────────────────────────────────────── */}
      {/* 3. THE 6 CORE ENGINES (CLEAN 3x2 ENTERPRISE GRID WITH UI PREVIEWS) */}
      {/* ──────────────────────────────────────────────────────────────────── */}
      <section
        id="core-engines"
        className="scroll-mt-24 py-20 sm:py-24 px-4 sm:px-6 lg:px-8 bg-[#FAF9F6] border-b border-black/[0.06]"
      >
        <div className="max-w-[1400px] mx-auto">
          {/* Section Header */}
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-orange-50/80 border border-orange-200/60 text-[#FF5A1F] text-xs font-mono font-bold uppercase tracking-wider mb-4">
              <span className="w-2 h-2 rounded-full bg-[#FF5A1F]" />
              <span>SIX CORE ENGINES</span>
            </div>
            <h2 className="text-3xl sm:text-5xl font-display font-extrabold text-[#0B0B0B] tracking-tight mb-3">
              Six Engines. <span className="text-[#FF5A1F]">One Unified Platform.</span>
            </h2>
            <p className="text-neutral-600 text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
              Everything your business needs to operate, secure and grow — in one platform.
            </p>
          </div>

          {/* Clean 3-Column by 2-Row Enterprise Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 sm:gap-7">
            {coreEngines.map((engine) => {
              const IconComp = engine.icon;
              return (
                <div
                  key={engine.id}
                  className="rounded-3xl bg-white border border-neutral-200/90 p-5 sm:p-6 shadow-xs hover:shadow-xl hover:border-neutral-300 transition-all duration-300 flex flex-col justify-between group text-left"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-5 items-stretch flex-1">
                    {/* Left Sub-Column: Text, Capabilities & Action */}
                    <div className="flex flex-col justify-between">
                      <div>
                        {/* Top: 01 Badge + Icon */}
                        <div className="flex items-center gap-2.5 mb-3">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-orange-50 border border-orange-200/70 text-[#FF5A1F] text-[11px] font-mono font-bold">
                            {engine.num}
                          </span>
                          <div className="w-8 h-8 rounded-xl bg-orange-50 border border-orange-100 flex items-center justify-center text-[#FF5A1F] shrink-0">
                            <IconComp className="w-4 h-4" />
                          </div>
                        </div>

                        {/* Category */}
                        <p className="text-[10px] font-mono font-bold uppercase tracking-wider text-neutral-400 mb-1">
                          {engine.category}
                        </p>

                        {/* Engine Title */}
                        <h3 className="text-base sm:text-lg font-display font-bold text-[#0B0B0B] group-hover:text-[#FF5A1F] transition-colors leading-snug mb-2">
                          {engine.title}
                        </h3>

                        {/* Single Sentence Explaining Business Value */}
                        <p className="text-xs text-neutral-500 leading-relaxed mb-4">
                          {engine.valueProp}
                        </p>

                        {/* 3 Short Capability Bullets with Custom Orange Icons */}
                        <div className="space-y-2 mb-4">
                          {engine.capabilities.map((cap, cIdx) => {
                            const CapIcon = cap.icon;
                            return (
                              <div
                                key={cIdx}
                                className="flex items-center gap-2 text-[11px] text-neutral-700 font-medium"
                              >
                                <span className="w-4 h-4 rounded-full bg-orange-50 border border-orange-200/60 flex items-center justify-center text-[#FF5A1F] shrink-0">
                                  <CapIcon className="w-2.5 h-2.5" />
                                </span>
                                <span className="leading-tight">{cap.label}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Card Action: Explore engine */}
                      <button
                        type="button"
                        onClick={() => setSelectedEngine(engine)}
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-[#FF5A1F] hover:text-[#ff6d38] group-hover:translate-x-1 transition-all cursor-pointer mt-3 pt-3 border-t border-neutral-100"
                        aria-label={`Explore ${engine.title}`}
                      >
                        <span>Explore engine</span>
                        <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    {/* Right Sub-Column: Authentic UI Micro-Preview */}
                    <div className="flex flex-col justify-center">
                      {renderEnginePreview(engine.id)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Progressive Disclosure: Accessible Engine Technical Detail Modal */}
        {selectedEngine && (
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-engine-title"
            className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-black/60 backdrop-blur-xs animate-in fade-in duration-200"
            onClick={() => setSelectedEngine(null)}
          >
            <div
              className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl bg-white p-6 sm:p-8 shadow-2xl border border-neutral-200 text-left animate-in zoom-in-95 duration-200"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                type="button"
                onClick={() => setSelectedEngine(null)}
                className="absolute top-5 right-5 w-8 h-8 rounded-full bg-neutral-100 hover:bg-neutral-200 grid place-items-center text-neutral-600 transition-colors cursor-pointer"
                aria-label="Close engine details"
              >
                <X className="w-4 h-4" />
              </button>

              {/* Modal Header */}
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-2xl bg-orange-50 border border-orange-100 flex items-center justify-center text-[#FF5A1F] shrink-0">
                  <selectedEngine.icon className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-[11px] font-mono font-bold uppercase tracking-wider text-neutral-400">
                    {selectedEngine.category}
                  </div>
                  <div className="text-xs font-mono font-bold text-[#FF5A1F]">
                    {selectedEngine.metricBadge}
                  </div>
                </div>
              </div>

              <h3 id="modal-engine-title" className="text-2xl font-display font-extrabold text-neutral-900 mb-2">
                {selectedEngine.title}
              </h3>
              <p className="text-sm text-neutral-600 mb-6 leading-relaxed">
                "{selectedEngine.tagline}"
              </p>

              {/* Preserved Detailed Technical Capabilities */}
              <div className="mb-6">
                <h4 className="text-xs font-mono font-bold uppercase tracking-wider text-neutral-400 mb-3">
                  Technical Capabilities & Architecture
                </h4>
                <div className="space-y-3">
                  {selectedEngine.bullets.map((b, bIdx) => (
                    <div key={bIdx} className="flex items-start gap-3 p-3.5 rounded-2xl bg-[#FAF9F6] border border-neutral-100">
                      <div className="w-5 h-5 rounded-full bg-emerald-50 text-emerald-600 grid place-items-center shrink-0 mt-0.5">
                        <Check className="w-3.5 h-3.5 stroke-[3]" />
                      </div>
                      <div>
                        <span className="text-xs font-bold text-neutral-900 block sm:inline">
                          {b.label}:{" "}
                        </span>
                        <span className="text-xs text-neutral-600 leading-relaxed">
                          {b.detail}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Preserved Live Terminal Telemetry */}
              <div className="mb-6 bg-[#0B0B0B] text-white rounded-2xl p-4 font-mono text-[11px] border border-white/10">
                <div className="flex items-center justify-between text-neutral-400 pb-2 mb-2 border-b border-white/10 text-[10px]">
                  <span>{selectedEngine.terminalPreview.title}</span>
                  <span className="text-emerald-400 font-semibold">● {selectedEngine.terminalPreview.status}</span>
                </div>
                <div className="text-[#FF5A1F] text-[10px] mb-2 font-semibold">
                  $ {selectedEngine.terminalPreview.command}
                </div>
                <div className="space-y-1 text-neutral-300 text-[10px]">
                  {selectedEngine.terminalPreview.logs.map((l, lIdx) => (
                    <div key={lIdx} className="truncate">
                      {l}
                    </div>
                  ))}
                </div>
              </div>

              {/* Modal Actions */}
              <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-neutral-100">
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedEngine(null);
                      openConsultationModal(`Architecture Demo - ${selectedEngine.title}`);
                    }}
                    className="px-6 py-2.5 rounded-full bg-[#FF5A1F] hover:bg-[#ff6d38] text-white font-bold text-xs transition-all shadow-md shadow-[#FF5A1F]/20 flex items-center gap-1.5 cursor-pointer"
                  >
                    <span>Schedule Architecture Demo</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>

                  {selectedEngine.dedicatedPageUrl && (
                    <Link
                      to={selectedEngine.dedicatedPageUrl}
                      onClick={() => setSelectedEngine(null)}
                      className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-orange-50 hover:bg-orange-100 text-[#FF5A1F] border border-orange-200 text-xs font-bold transition-all cursor-pointer"
                    >
                      <span>Explore Dedicated Guardian SRE Page</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => setSelectedEngine(null)}
                  className="px-4 py-2.5 rounded-full bg-neutral-100 hover:bg-neutral-200 text-neutral-700 font-semibold text-xs transition-colors cursor-pointer"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* ──────────────────────────────────────────────────────────────────── */}
      {/* 4. VISUAL WORKFLOW: "FROM EVENT TO BUSINESS OUTCOME"                  */}
      {/* ──────────────────────────────────────────────────────────────────── */}
      {/* ──────────────────────────────────────────────────────────────────── */}
      {/* 4. VISUAL WORKFLOW: "ONE EVENT. REAL BUSINESS OUTCOMES."             */}
      {/* ──────────────────────────────────────────────────────────────────── */}
      <section
        id="workflow"
        className="scroll-mt-24 py-16 sm:py-20 px-4 sm:px-8 bg-[#FAFAFA] border-b border-black/[0.06]"
      >
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center max-w-3xl mx-auto mb-10">
            <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-orange-50 border border-orange-200/70 text-[#FF5A1F] text-xs font-mono font-bold uppercase tracking-wider mb-4 shadow-2xs">
              <span className="w-2 h-2 rounded-full bg-[#FF5A1F]" />
              <span>AUTOMATED WORKFLOW EXPLORER</span>
            </div>
            <h2 className="text-3xl sm:text-5xl font-display font-black text-[#0B0B0B] tracking-tight mb-4">
              One Event. Real Business Outcomes.
            </h2>
            <p className="text-neutral-500 text-sm sm:text-base leading-relaxed max-w-2xl mx-auto">
              Explore how nORBIT connects your business operations across sales, delivery, payments, workforce and infrastructure — with automated workflows and zero manual handoffs.
            </p>
          </div>

          {/* 5 Domain Selector Tabs (Matching User Figma Mockup) */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-8">
            {workflowScenarios.map((sc, sIdx) => {
              const isSelected = selectedScenarioIndex === sIdx;
              const DomainIcon = sc.icon;
              return (
                <button
                  key={sIdx}
                  type="button"
                  onClick={() => {
                    setSelectedScenarioIndex(sIdx);
                    setActiveWorkflowStep(0);
                  }}
                  className={`text-left p-3.5 sm:p-4 rounded-2xl transition-all cursor-pointer border flex items-center gap-3.5 ${
                    isSelected
                      ? "bg-[#0B0B0B] text-white border-[#0B0B0B] shadow-sm"
                      : "bg-white text-neutral-800 border-neutral-200/80 hover:border-neutral-300 shadow-2xs"
                  }`}
                >
                  <div className="shrink-0">
                    <DomainIcon className={`w-5 h-5 sm:w-6 sm:h-6 ${isSelected ? "text-amber-500" : "text-[#FF5A1F]"}`} />
                  </div>
                  <div className="min-w-0">
                    <div className={`text-xs sm:text-sm font-bold leading-tight truncate ${isSelected ? "text-white" : "text-neutral-900"}`}>
                      {sc.title}
                    </div>
                    <div className={`text-[11px] mt-0.5 truncate ${isSelected ? "text-neutral-400" : "text-neutral-400"}`}>
                      {sc.desc}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Master-Detail Interactive Workflow Area */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-stretch mb-6">
            
            {/* Left: 4 Sequence Cards connected with Arrows */}
            <div className="lg:col-span-7 flex flex-col justify-between">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-2 relative items-center h-full">
                {workflowScenarios[selectedScenarioIndex].steps.map((st, idx) => {
                  const isSelected = activeWorkflowStep === idx;
                  const StepIcon = st.icon;
                  return (
                    <div key={idx} className="relative flex items-center h-full">
                      {/* Card */}
                      <button
                        type="button"
                        onClick={() => setActiveWorkflowStep(idx)}
                        className={`w-full h-full p-4 rounded-2xl text-left transition-all cursor-pointer flex flex-col justify-between border relative min-h-[250px] ${
                          isSelected
                            ? "bg-white border-orange-400 shadow-sm ring-2 ring-orange-100"
                            : "bg-white border-neutral-200/80 hover:border-neutral-300 shadow-2xs"
                        }`}
                      >
                        <div>
                          {/* Top Number Badge */}
                          <div className="flex items-center justify-between mb-4">
                            <span className={`text-[11px] font-mono px-2 py-0.5 rounded-md ${
                              isSelected ? "bg-orange-50 text-[#FF5A1F] font-bold" : "bg-neutral-100 text-neutral-500 font-medium"
                            }`}>
                              {st.num}
                            </span>
                          </div>

                          {/* Icon Squircle */}
                          <div className={`w-12 h-12 rounded-2xl ${st.iconBg} ${st.iconColor} flex items-center justify-center mb-3`}>
                            <StepIcon className="w-6 h-6" />
                          </div>

                          {/* Title & Subtitle */}
                          <h4 className="text-sm font-bold text-neutral-900 mb-1 leading-snug">
                            {st.title}
                          </h4>
                          <p className="text-[11px] text-neutral-400 leading-tight">
                            {st.desc}
                          </p>
                        </div>

                        {/* Status Pill */}
                        <div className="pt-3">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                            st.statusType === "orange"
                              ? "bg-orange-50 text-[#FF5A1F] border border-orange-200/60"
                              : "bg-emerald-50 text-emerald-700 border border-emerald-200/60"
                          }`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${st.statusType === "orange" ? "bg-[#FF5A1F]" : "bg-emerald-500"}`} />
                            <span>{st.status}</span>
                          </span>
                        </div>
                      </button>

                      {/* Connector Arrow for desktop (between cards) */}
                      {idx < 3 && (
                        <div className="hidden sm:flex absolute -right-3.5 top-1/2 -translate-y-1/2 z-20 pointer-events-none">
                          <div className="w-6 h-6 rounded-full bg-white border border-orange-200 shadow-2xs flex items-center justify-center text-[#FF5A1F] text-[10px] font-bold">
                            ➔
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right: Step Detail Card */}
            <div className="lg:col-span-5">
              <div className="rounded-3xl bg-white border border-neutral-200/90 p-6 sm:p-7 shadow-2xs h-full flex flex-col justify-between">
                <div>
                  {/* Top Step Bar */}
                  <div className="flex items-center justify-between pb-4 mb-4 border-b border-neutral-100">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-[#FF5A1F]" />
                      <span className="text-xs font-mono font-bold uppercase tracking-wider text-neutral-600">
                        STEP {workflowScenarios[selectedScenarioIndex].steps[activeWorkflowStep].num}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setCopiedIndex(999);
                        setTimeout(() => setCopiedIndex(null), 2000);
                      }}
                      className="px-3 py-1 rounded-full text-xs font-semibold bg-orange-50 text-[#FF5A1F] border border-orange-200/80 hover:bg-orange-100 transition-all cursor-pointer"
                    >
                      {copiedIndex === 999 ? "Event Emitted!" : "Trigger Event"}
                    </button>
                  </div>

                  {/* Title & Description */}
                  <h3 className="text-xl font-bold text-neutral-900 mb-2 leading-tight">
                    {workflowScenarios[selectedScenarioIndex].steps[activeWorkflowStep].detailTitle}
                  </h3>
                  <p className="text-xs text-neutral-500 leading-relaxed mb-6">
                    {workflowScenarios[selectedScenarioIndex].steps[activeWorkflowStep].detailDesc}
                  </p>

                  {/* Metadata Rows with divider lines */}
                  <div className="space-y-3 pt-2 text-xs">
                    <div className="flex items-start justify-between py-1.5 border-b border-neutral-100">
                      <div className="flex items-center gap-2 text-neutral-400 font-medium">
                        <Zap className="w-3.5 h-3.5 text-[#FF5A1F]" />
                        <span>Trigger</span>
                      </div>
                      <div className="font-semibold text-neutral-800 text-right">
                        {workflowScenarios[selectedScenarioIndex].steps[activeWorkflowStep].trigger}
                      </div>
                    </div>

                    <div className="flex items-start justify-between py-1.5 border-b border-neutral-100">
                      <div className="flex items-center gap-2 text-neutral-400 font-medium">
                        <Database className="w-3.5 h-3.5 text-[#FF5A1F]" />
                        <span>Source</span>
                      </div>
                      <div className="font-semibold text-neutral-800 text-right">
                        {workflowScenarios[selectedScenarioIndex].steps[activeWorkflowStep].source}
                      </div>
                    </div>

                    <div className="flex items-start justify-between py-1.5 border-b border-neutral-100">
                      <div className="flex items-center gap-2 text-neutral-400 font-medium">
                        <FileText className="w-3.5 h-3.5 text-[#FF5A1F]" />
                        <span>Creates</span>
                      </div>
                      <div className="font-semibold text-neutral-800 text-right">
                        {workflowScenarios[selectedScenarioIndex].steps[activeWorkflowStep].creates}
                      </div>
                    </div>

                    <div className="flex items-start justify-between py-1.5">
                      <div className="flex items-center gap-2 text-neutral-400 font-medium">
                        <Mail className="w-3.5 h-3.5 text-[#FF5A1F]" />
                        <span>Notification</span>
                      </div>
                      <div className="font-semibold text-neutral-800 text-right">
                        {workflowScenarios[selectedScenarioIndex].steps[activeWorkflowStep].notification}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* Bottom: Recent Events Stream (Exact Match to User Screenshot) */}
          <div className="rounded-2xl bg-white border border-neutral-200/80 p-5 shadow-2xs">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-3 mb-4 border-b border-neutral-100 gap-2">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-[#FF5A1F]" />
                <span className="text-xs sm:text-sm font-bold text-neutral-900">Recent Events</span>
                <span className="text-[11px] text-neutral-400 font-normal">Live execution log (illustrative)</span>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-[#FF5A1F]">
                  <span className="w-2 h-2 rounded-full bg-[#FF5A1F] animate-pulse" />
                  <span>Live</span>
                </div>
                <button
                  type="button"
                  onClick={() => openConsultationModal("Workflow Events Stream")}
                  className="text-xs font-medium text-neutral-500 hover:text-neutral-900 flex items-center gap-1 cursor-pointer"
                >
                  <span>View all events</span>
                  <span>➔</span>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {workflowScenarios[selectedScenarioIndex].recentEvents.map((ev, evIdx) => (
                <div key={evIdx} className="flex items-start gap-2.5">
                  <span className={`w-2 h-2 rounded-full ${ev.dot === "orange" ? "bg-[#FF5A1F]" : "bg-emerald-500"} mt-1.5 shrink-0`} />
                  <div>
                    <div className="text-[10px] font-mono text-neutral-400">{ev.time}</div>
                    <div className="text-xs font-bold text-neutral-800 leading-snug">{ev.title}</div>
                    <div className="text-[11px] text-neutral-400 leading-snug">{ev.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────────────── */}
      {/* 5. SOVEREIGN DEPLOYMENT TOPOLOGIES                                   */}
      {/* ──────────────────────────────────────────────────────────────────── */}
      <section
        id="deployments"
        className="scroll-mt-24 py-20 sm:py-24 px-4 sm:px-8 bg-[#FAF9F6] border-b border-black/[0.06]"
      >
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-500/10 text-purple-600 text-[11px] font-mono font-bold uppercase tracking-wider mb-3">
              DEPLOYMENT TOPOLOGIES
            </div>
            <h2 className="text-3xl sm:text-5xl font-display font-extrabold text-[#0B0B0B] tracking-tight mb-4">
              Deploy Anywhere. Own Everything.
            </h2>
            <p className="text-neutral-600 text-sm sm:text-base">
              Run on your choice of infrastructure with 100% data sovereignty. We install and configure on your private cloud, dedicated bare metal, or on-premise hardware.
            </p>
          </div>

          {/* Deployment Cards */}
          <div className="grid md:grid-cols-3 gap-6">
            {deploymentTopologies.map((top, idx) => (
              <div
                key={idx}
                className={`rounded-3xl p-7 flex flex-col justify-between transition-all duration-300 ${top.featured
                    ? "bg-neutral-900 text-white border-2 border-[#FF5A1F] shadow-2xl relative"
                    : "bg-white text-neutral-800 border border-black/10 hover:border-black/20 hover:shadow-lg"
                  }`}
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span
                      className={`text-xs font-mono font-bold px-2.5 py-1 rounded-full ${top.featured
                          ? "bg-[#FF5A1F] text-white"
                          : "bg-neutral-100 text-neutral-700"
                        }`}
                    >
                      {top.badge}
                    </span>
                  </div>

                  <h3 className="text-2xl font-display font-bold mb-1">
                    {top.type}
                  </h3>
                  <div
                    className={`text-xs font-mono mb-4 ${top.featured ? "text-neutral-400" : "text-neutral-500"
                      }`}
                  >
                    {top.subtitle}
                  </div>
                  <p
                    className={`text-xs sm:text-sm leading-relaxed mb-6 ${top.featured ? "text-neutral-300" : "text-neutral-600"
                      }`}
                  >
                    {top.desc}
                  </p>

                  <div className="space-y-2.5 mb-8">
                    {top.specs.map((sp, spIdx) => (
                      <div key={spIdx} className="flex items-center gap-2 text-xs">
                        <Check
                          className={`w-4 h-4 shrink-0 ${top.featured ? "text-[#FF5A1F]" : "text-emerald-600"
                            }`}
                        />
                        <span>{sp}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <button
                  onClick={() => openConsultationModal(`Deploy Topology: ${top.type}`)}
                  className={`w-full py-3.5 rounded-full text-xs font-bold transition-all duration-300 cursor-pointer ${top.featured
                      ? "bg-[#FF5A1F] hover:bg-[#ff6d38] text-white shadow-lg shadow-[#FF5A1F]/30"
                      : "bg-neutral-100 hover:bg-neutral-200 text-neutral-900"
                    }`}
                >
                  Select {top.type} →
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────────────── */}
      {/* 6. BOTTOM CALL TO ACTION (CTA)                                       */}
      {/* ──────────────────────────────────────────────────────────────────── */}
      <section className="py-20 sm:py-28 px-4 sm:px-8 bg-gradient-to-b from-white to-[#FAF9F6] text-center border-t border-black/[0.06]">
        <div className="max-w-4xl mx-auto">
          {/* Eyebrow */}
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#FF5A1F]/10 border border-[#FF5A1F]/20 text-[#FF5A1F] text-xs font-mono font-bold uppercase tracking-wider mb-6">
            ✦ DEPLOY SOVEREIGN ENTERPRISE OS
          </div>

          {/* Main Headline */}
          <h2 className="text-3xl sm:text-5xl lg:text-6xl font-display font-extrabold text-[#0B0B0B] tracking-tight leading-[1.1] mb-6">
            Stop Paying Seat Taxes to 8 Different SaaS Platforms.
          </h2>

          {/* Sub-headline */}
          <p className="text-neutral-600 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed mb-10">
            Deploy nORBIT sovereignly on your private cloud. Your code, your data, your entire business under one orbit.
          </p>

          {/* CTAs */}
          <div className="flex flex-wrap items-center justify-center gap-4 mb-8">
            <button
              onClick={() => openConsultationModal("Schedule Private Architecture Demo")}
              className="px-8 py-4 rounded-full bg-[#FF5A1F] hover:bg-[#ff6d38] text-white font-bold text-sm transition-all duration-300 shadow-xl shadow-[#FF5A1F]/30 flex items-center gap-2 group cursor-pointer"
            >
              <span>Schedule Private Architecture Demo</span>
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>

            <a
              href="https://teams.neuravolt.in"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-4 rounded-full bg-white hover:bg-neutral-100 text-neutral-800 font-semibold text-sm transition-all duration-300 border border-black/10 shadow-xs flex items-center gap-2 cursor-pointer"
            >
              <span>teams.neuravolt.in</span>
              <ArrowUpRight className="w-4 h-4 text-neutral-500" />
            </a>
          </div>

          {/* Bottom Security Assurance */}
          <div className="flex flex-wrap items-center justify-center gap-6 text-xs text-neutral-500 font-mono">
            <span>🔒 DPDP-2023 Compliant</span>
            <span>•</span>
            <span>⚡ Zero Per-User Fees</span>
            <span>•</span>
            <span>🏢 Complete Source & DB Sovereignty</span>
          </div>
        </div>
      </section>
    </Layout>
  );
}
